import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  ArrowLeft, Save, Send, Copy as CopyIcon, ArrowLeftRight,
  Mail, Trash2, RefreshCcw, MapPin, Plane, Clock, FileText,
  DollarSign, History as HistoryIcon, Bell, Eye, BookOpen, ShieldCheck,
  X, User, Car, MessageSquare, ExternalLink, Pencil,
} from "lucide-react";
import TripChat from "@/components/TripChat";
import PaymentSelector from "@/components/PaymentSelector";
import FileUploadSection from "@/components/FileUploadSection";
import { formatPickupLocal } from "@/lib/datetime";
import RoutePreviewMap from "@/components/RoutePreviewMap";
import { BalanceDueBanner, ChargeBalanceModal } from "@/components/BalanceDueBanner";
import { StaffThemeProvider, StaffThemeToggle } from "@/lib/StaffTheme";

const STATUS_META = {
  pending:               { label: "Pending",             color: "#A1A1AA" },
  new:                   { label: "Pending (legacy)",    color: "#A1A1AA" },
  accepted:              { label: "Accepted",            color: "#3B82F6" },
  assigned:              { label: "Accepted (legacy)",   color: "#3B82F6" },
  dispatched:            { label: "Dispatched",          color: "#6366F1" },
  accepted_by_driver:    { label: "Driver accepted",     color: "#8B5CF6" },
  en_route:              { label: "En route",            color: "#F59E0B" },
  on_location:           { label: "On location",         color: "#EC4899" },
  customer_in_car:       { label: "Customer in car",     color: "#D4AF37" },
  in_progress:           { label: "In progress (legacy)",color: "#D4AF37" },
  clear_at_destination:  { label: "Clear at destination",color: "#06B6D4" },
  completed:             { label: "Completed",           color: "#10B981" },
  cancelled:             { label: "Cancelled",           color: "#EF4444" },
  paused:                { label: "Paused",              color: "#71717A" },
};

const STATUS_META_FULL = STATUS_META;

const TABS = [
  { k: "overview",  l: "Overview",   icon: BookOpen },
  { k: "payments",  l: "Payments",   icon: DollarSign },
  { k: "invoicing", l: "Invoicing",  icon: FileText },
  { k: "settlement",l: "Settlement", icon: DollarSign },
  { k: "history",   l: "History",    icon: HistoryIcon },
  { k: "timeline",  l: "Timeline",   icon: Clock },
  { k: "reminders", l: "Reminders",  icon: Bell },
];

export default function ReservationDetail() {
  return (
    <StaffThemeProvider>
      <ReservationDetailInner/>
    </StaffThemeProvider>
  );
}

function ReservationDetailInner() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");
  const [booking, setBooking] = useState(null);
  const [driver, setDriver] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showCustomerView, setShowCustomerView] = useState(false);
  const [showCommunication, setShowCommunication] = useState(false);
  const [showRoundTrip, setShowRoundTrip] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/bookings/${bookingId}/full`);
      setBooking(r.data.booking);
      setDriver(r.data.driver);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load reservation");
    }
  }, [bookingId]);

  useEffect(() => {
    if (!loading && (!user || user.role !== "dispatcher")) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => { if (user?.role === "dispatcher") refresh(); }, [user, refresh]);

  useEffect(() => {
    (async () => {
      try {
        const [d, v] = await Promise.all([api.get("/drivers"), api.get("/vehicles?staff=1")]);
        setDrivers(d.data || []); setVehicles(v.data || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  if (!user || user.role !== "dispatcher") return null;
  if (!booking) return <div className="min-h-screen bg-[#0a0a0a] text-[#F5F2EB] p-12">Loading…</div>;

  // -------- Top-bar actions --------
  const setStatus = async (status) => {
    setSaving(true);
    try {
      await api.patch(`/bookings/${booking.booking_id}/status`, { status });
      toast.success(`Status → ${status}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const assignDriver = async (driver_id) => {
    if (!driver_id) return;
    setSaving(true);
    try {
      await api.patch(`/bookings/${booking.booking_id}/assign`, { driver_id });
      toast.success("Driver assigned");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const resendConfirmation = async () => {
    try { await api.post(`/bookings/${booking.booking_id}/resend-confirmation`); toast.success("Confirmation re-sent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const cloneRoundTrip = async (returnDatetime) => {
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/round-trip`,
        returnDatetime ? { return_pickup_datetime: returnDatetime } : {});
      toast.success(`Return trip created · ${r.data.confirmation_code}`);
      setShowRoundTrip(false);
      navigate(`/dispatcher/reservation/${r.data.booking_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const copyTrackLink = () => {
    const url = `${window.location.origin}/track?code=${booking.confirmation_code}&email=${encodeURIComponent(booking.customer_email)}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Tracking link copied"));
  };
  const openTrack = () => {
    const url = `/track?code=${booking.confirmation_code}&email=${encodeURIComponent(booking.customer_email)}`;
    window.open(url, "_blank");
  };
  const del = async () => {
    const code = booking.confirmation_code || booking.booking_id;
    if (!window.confirm(`Permanently delete reservation ${code}?\n\nThis removes the booking, driver locations, and chat history. Past SMS/email logs are kept for audit.\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/bookings/${booking.booking_id}`);
      toast.success(`Reservation ${code} deleted`);
      navigate("/dispatcher");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const statusColor = STATUS_META[booking.status]?.color || "#A1A1AA";

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F2EB]" data-testid="reservation-detail">
      {/* Top status banner */}
      <div className="px-6 md:px-10 py-3 flex justify-between items-center" style={{ background: `${statusColor}15`, borderBottom: `1px solid ${statusColor}40` }}>
        <div className="flex items-center gap-3">
          <Link to="/dispatcher" data-testid="rd-back-link" className="text-[#A1A1AA] hover:text-[#F5F2EB]"><ArrowLeft size={18}/></Link>
          <div className="font-serif text-xl">Reservation <span style={{ color: statusColor }}>#{booking.confirmation_code}</span></div>
          <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border" style={{ color: statusColor, borderColor: `${statusColor}60` }}>
            {STATUS_META[booking.status]?.label || booking.status}
          </span>
          <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border border-[#D4AF37]/40 text-[#D4AF37]">
            {booking.trip_type === "hourly" ? "Hourly" : "P2P"}
          </span>
          {booking.payment_status === "balance_due" && (
            <span data-testid="rd-balance-due-badge" className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border border-amber-500/60 text-amber-300 bg-amber-500/10">
              Partially paid · Balance due ${((booking.balance_due_cents || 0) / 100).toFixed(2)}
            </span>
          )}
          {booking.payment_status === "paid" && (booking.supplemental_charges || []).length > 0 && (
            <span data-testid="rd-paid-badge" className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border border-emerald-500/50 text-emerald-300">Paid · incl. supplemental</span>
          )}
          <span data-testid="rd-header-pickup-tz" className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] hidden md:inline-flex items-center gap-1.5" title="Business timezone (America/Chicago)">
            <Clock size={11} className="text-[#D4AF37]"/>
            {formatPickupLocal(booking.pickup_datetime)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <StaffThemeToggle compact/>
          <button data-testid="rd-refresh" onClick={refresh} className="btn-ghost !py-1.5 !px-3 text-[10px]"><RefreshCcw size={12}/> Refresh</button>
          <Link to="/dispatcher" className="btn-ghost !py-1.5 !px-3 text-[10px]">× Back</Link>
        </div>
      </div>

      {/* Top action bar */}
      <div className="px-6 md:px-10 py-4 border-b border-white/10 flex flex-wrap justify-between items-center gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="rd-communication" onClick={() => setShowCommunication(true)} className="btn-ghost !py-1.5 !px-3 text-[10px]"><Send size={12}/> Communication</button>
          <button data-testid="rd-copy" onClick={copyTrackLink} className="btn-ghost !py-1.5 !px-3 text-[10px]"><CopyIcon size={12}/> Copy</button>
          <button data-testid="rd-track" onClick={openTrack} className="btn-ghost !py-1.5 !px-3 text-[10px]"><MapPin size={12}/> Track</button>
          <button data-testid="rd-round-trip" onClick={() => setShowRoundTrip(true)} className="btn-ghost !py-1.5 !px-3 text-[10px]"><ArrowLeftRight size={12}/> Round Trip</button>
          <button data-testid="rd-confirmation-email" onClick={resendConfirmation} className="btn-ghost !py-1.5 !px-3 text-[10px]"><Mail size={12}/> Confirmation Email</button>
          <a data-testid="rd-trip-manifest" href={`${API}/bookings/${booking.confirmation_code}/manifest.pdf`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10"><ShieldCheck size={12} className="inline mr-1"/>Trip Manifest</a>
          <button data-testid="rd-customer-view" onClick={() => setShowCustomerView(true)} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10"><Eye size={12} className="inline mr-1"/>Customer View</button>
          <a
            data-testid="rd-view-as-customer"
            href={`/track?code=${booking.confirmation_code}&contact=${encodeURIComponent(booking.customer_email || booking.customer_phone || "")}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Opens the public Track page in a new tab as the customer would see it (their auth, their data, their view)."
            className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-sky-300 border border-sky-500/40 hover:bg-sky-500/10"
          ><ExternalLink size={12} className="inline mr-1"/>View as Customer</a>
          <button data-testid="rd-delete" onClick={del} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-red-400 border border-red-500/40 hover:bg-red-500/10"><Trash2 size={12} className="inline mr-1"/>Delete</button>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">Status</label>
            <select data-testid="rd-status-select" value={booking.status} onChange={e => setStatus(e.target.value)} disabled={saving}
              className="bg-[#0a0a0a] border border-white/10 px-2 py-1.5 text-xs">
              {Object.entries(STATUS_META)
                .filter(([_, m]) => !m.label.includes("(legacy)"))
                .map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">Driver</label>
            <select data-testid="rd-driver-select" value={booking.assigned_driver_id || ""} onChange={e => assignDriver(e.target.value)} disabled={saving}
              className="bg-[#0a0a0a] border border-white/10 px-2 py-1.5 text-xs min-w-[160px]">
              <option value="">— Unassigned —</option>
              {drivers.map(d => <option key={d.driver_id} value={d.driver_id}>{d.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex">
        {/* Left sidebar tabs */}
        <aside className="w-56 border-r border-white/10 py-6 px-3 sticky top-0 self-start">
          {TABS.map(({ k, l, icon: Icon }) => (
            <button key={k} data-testid={`rd-tab-${k}`} onClick={() => setTab(k)}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-xs uppercase tracking-[0.18em] mb-1 border-l-2 transition-colors ${
                tab === k
                  ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A] font-semibold shadow-[0_0_14px_rgba(212,175,55,0.35)]"
                  : "border-transparent text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F2EB]"
              }`}>
              <Icon size={13}/> {l}
            </button>
          ))}
        </aside>

        {/* Main */}
        <main className="flex-1 p-6 md:p-8 grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-6">
            {tab === "overview" && <OverviewTab booking={booking} vehicles={vehicles} onSaved={refresh}/>}
            {tab === "overview" && (
              <FileUploadSection mode="trip_attachment" bookingId={booking.booking_id} apiClient={api} canDelete title="Trip attachments"/>
            )}
            {tab === "payments" && <PaymentsTab bookingId={booking.booking_id} booking={booking} onBookingChanged={refresh}/>}
            {tab === "invoicing" && <InvoicingTab booking={booking} onSaved={refresh}/>}
            {tab === "settlement" && <SettlementInline booking={booking} onChange={refresh}/>}
            {tab === "history" && <HistoryTab bookingId={booking.booking_id}/>}
            {tab === "timeline" && <TimelineTab bookingId={booking.booking_id}/>}
            {tab === "reminders" && <RemindersTab bookingId={booking.booking_id} booking={booking} onChange={refresh}/>}
          </div>
          {/* Pricing panel - sticky right column */}
          <div className="xl:col-span-1">
            <div className="sticky top-4">
              <PricingPanel booking={booking} onSaved={refresh}/>
            </div>
          </div>
        </main>
      </div>

      {showCustomerView && <CustomerViewPanel bookingId={booking.booking_id} onClose={() => setShowCustomerView(false)}/>}
      {showCommunication && <CommunicationModal booking={booking} onClose={() => setShowCommunication(false)}/>}
      {showRoundTrip && <RoundTripModal booking={booking} onCreate={cloneRoundTrip} onClose={() => setShowRoundTrip(false)}/>}
    </div>
  );
}

// =============================================================
// OVERVIEW
// =============================================================
function OverviewTab({ booking, vehicles, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const [first, ...rest] = (booking.customer_name || "").split(" ");
    setForm({
      first_name: first || "",
      last_name: rest.join(" ") || "",
      customer_phone: booking.customer_phone || "",
      customer_email: booking.customer_email || "",
      pickup_date: (booking.pickup_datetime || "").slice(0, 10),
      pickup_time: (booking.pickup_datetime || "").slice(11, 16),
      pax: booking.pax || 1,
      vehicle_id: booking.vehicle_id || "",
      pickup_address: booking.pickup_address || "",
      dropoff_address: booking.dropoff_address || "",
      stops: [...(booking.stops || [])],
      flight_number: booking.flight_number || "",
      notes: booking.notes || "",
      internal_notes: booking.internal_notes || "",
      child_seat: !!booking.child_seat,
      gate_meet: !!booking.gate_meet,
      meet_and_greet: !!booking.meet_and_greet,
      share_driver_info_with_customer: booking.share_driver_info_with_customer !== false,
      hide_pricing_from_driver: booking.hide_pricing_from_driver !== false,
      hide_price_on_tracking: !!booking.hide_price_on_tracking,
      suppress_customer_sms: !!booking.suppress_customer_sms,
      guest_name: booking.guest_name || "",
      guest_phone: booking.guest_phone || "",
      guest_count: booking.guest_count || "",
    });
  }, [booking]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const pickup_datetime = form.pickup_date && form.pickup_time
        ? `${form.pickup_date}T${form.pickup_time}:00`
        : undefined;
      const customer_name = `${form.first_name} ${form.last_name}`.trim();
      const payload = {
        customer_name, customer_phone: form.customer_phone, customer_email: form.customer_email,
        pickup_datetime, pickup_address: form.pickup_address, dropoff_address: form.dropoff_address || null,
        stops: (form.stops || []).map(s => s.trim()).filter(Boolean),
        flight_number: form.flight_number || null, notes: form.notes || null,
        internal_notes: form.internal_notes || null,
        pax: Number(form.pax) || 1, vehicle_id: form.vehicle_id,
        child_seat: form.child_seat, gate_meet: form.gate_meet,
        meet_and_greet: form.meet_and_greet,
        share_driver_info_with_customer: form.share_driver_info_with_customer,
        hide_pricing_from_driver: form.hide_pricing_from_driver,
        hide_price_on_tracking: form.hide_price_on_tracking,
        suppress_customer_sms: form.suppress_customer_sms,
        guest_name: form.guest_name || "",
        guest_phone: form.guest_phone || "",
        guest_count: Number(form.guest_count) || null,
        notify_customer: true,
      };
      await api.patch(`/bookings/${booking.booking_id}/edit`, payload);
      toast.success("Reservation updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="rd-overview">
      <Section title="Customer">
        <div className="mb-3 inline-flex items-center gap-2 border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-3 py-1.5">
          <Clock size={12} className="text-[#D4AF37]"/>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">Scheduled pickup</span>
          <span className="text-xs text-[#F5F2EB] font-medium" data-testid="rd-pickup-local">
            {formatPickupLocal(booking.pickup_datetime)}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Input testid="of-first" label="First Name" value={form.first_name} onChange={v => upd("first_name", v)}/>
          <Input testid="of-last" label="Last Name" value={form.last_name} onChange={v => upd("last_name", v)}/>
          <Input testid="of-date" label="Pickup Date" type="date" value={form.pickup_date} onChange={v => upd("pickup_date", v)}/>
          <Input testid="of-time" label="Pickup Time" type="time" value={form.pickup_time} onChange={v => upd("pickup_time", v)}/>
          <Input testid="of-pax" label="Passengers" type="number" value={form.pax} onChange={v => upd("pax", v)}/>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <Input testid="of-phone" label="Phone" value={form.customer_phone} onChange={v => upd("customer_phone", v)}/>
          <Input testid="of-email" label="Email" value={form.customer_email} onChange={v => upd("customer_email", v)}/>
          <ReadOnlyField label="Reservation Type" value={booking.trip_type === "hourly" ? "Hourly" : "Point to Point"}/>
          <Select testid="of-vehicle" label="Vehicle" value={form.vehicle_id} onChange={v => upd("vehicle_id", v)} options={vehicles.map(v => [v.vehicle_id, v.name])}/>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <Checkbox testid="of-share-driver" label="Share driver info with customer" checked={form.share_driver_info_with_customer} onChange={v => upd("share_driver_info_with_customer", v)}/>
          <Checkbox testid="of-hide-pricing" label="$ Hide pricing from driver" checked={form.hide_pricing_from_driver} onChange={v => upd("hide_pricing_from_driver", v)}/>
          <Checkbox testid="of-hide-track-price" label="Hide price on tracking page (guest trips)" checked={form.hide_price_on_tracking} onChange={v => upd("hide_price_on_tracking", v)}/>
          <Checkbox testid="of-mute-customer-sms" label="Mute customer status texts (event rides)" checked={form.suppress_customer_sms} onChange={v => upd("suppress_customer_sms", v)}/>
          <Checkbox testid="of-child-seat" label="Child Seat" checked={form.child_seat} onChange={v => upd("child_seat", v)}/>
          <Checkbox testid="of-meet-greet" label="Meet & Greet (+$20)" checked={form.meet_and_greet} onChange={v => upd("meet_and_greet", v)}/>
        </div>
        <div className="mt-4 border-t border-white/10 pt-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-2">
            Guest rider info <span className="text-[#71717A] normal-case tracking-normal">(when the booker isn't the passenger — shown on tracking &amp; driver pages)</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Input testid="of-guest-name" label="Guest Name(s)" value={form.guest_name} onChange={v => upd("guest_name", v)}/>
            <Input testid="of-guest-phone" label="Guest Phone" value={form.guest_phone} onChange={v => upd("guest_phone", v)}/>
            <Input testid="of-guest-count" label="Number of Guests" type="number" value={form.guest_count} onChange={v => upd("guest_count", v)}/>
            <div className="flex items-end">
              <button data-testid="of-send-guest-track"
                disabled={!(form.guest_phone || "").trim()}
                title={!(form.guest_phone || "").trim() ? "Add a guest phone first (and save)" : `Text the live-tracking link to ${form.guest_phone}`}
                onClick={async () => {
                  try {
                    const r = await api.post(`/bookings/${booking.booking_id}/send-guest-track-sms`);
                    toast.success(`Tracking link texted to ${r.data.to}`);
                  } catch (e) {
                    const msg = e?.response?.data?.detail || "SMS failed";
                    if (msg.includes("test_number")) toast.warning("Not sent — 555 test numbers are blocked to avoid SMS charges");
                    else toast.error(msg);
                  }
                }}
                className="text-[10px] uppercase tracking-[0.14em] text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 disabled:opacity-40 px-3 py-2 w-full">
                Text link to guest
              </button>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Route Information" actions={
        <a href={`/track?code=${booking.confirmation_code}&email=${encodeURIComponent(booking.customer_email)}`} target="_blank" rel="noreferrer"
          className="btn-ghost !py-1.5 !px-3 text-[10px]"><Eye size={12}/> View Trip Tracking</a>
      }>
        <div className="grid grid-cols-1 gap-2">
          <Input testid="of-pickup-addr" label="Pickup" value={form.pickup_address} onChange={v => upd("pickup_address", v)}/>
          {/* Stops — editable at ANY status, incl. mid-trip when the customer
              asks the driver for an extra stop and the driver calls dispatch. */}
          {(form.stops || []).map((s, i) => (
            <div key={i} className="flex items-end gap-2" data-testid={`of-stop-row-${i}`}>
              <div className="flex-1">
                <Input testid={`of-stop-${i}`} label={`Stop ${i + 1}`} value={s}
                  onChange={v => upd("stops", form.stops.map((x, j) => (j === i ? v : x)))}/>
              </div>
              <button type="button" data-testid={`of-stop-remove-${i}`}
                onClick={() => upd("stops", form.stops.filter((_, j) => j !== i))}
                title="Remove stop"
                className="mb-1 px-2.5 py-1.5 text-red-400 border border-red-500/30 hover:bg-red-500/10 text-xs">×</button>
            </div>
          ))}
          <button type="button" data-testid="of-add-stop"
            onClick={() => upd("stops", [...(form.stops || []), ""])}
            className="self-start text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1.5 hover:bg-[#D4AF37]/5">
            + Add stop
          </button>
          {/* Driver-reported stop wait times — bill waiting via a Misc charge */}
          {(booking.stop_events || []).length > 0 && (
            <div className="border border-white/10 bg-white/[0.02] p-3 space-y-1" data-testid="stop-wait-times">
              <div className="text-[9px] uppercase tracking-[0.22em] text-[#D4AF37]">Driver stop times</div>
              {(booking.stop_events || []).map((ev, i) => {
                const fmtT = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—";
                const liveMin = ev.arrived_at && !ev.departed_at
                  ? Math.max(0, Math.round((Date.now() - new Date(ev.arrived_at).getTime()) / 60000))
                  : null;
                return (
                  <div key={i} className="text-xs text-[#A1A1AA]" data-testid={`stop-wait-${ev.stop_index}`}>
                    <span className="text-[#F5F2EB]">Stop {ev.stop_index + 1}</span> · arrived {fmtT(ev.arrived_at)}
                    {ev.departed_at
                      ? <> · departed {fmtT(ev.departed_at)} · <span className="text-[#D4AF37] font-bold">{Math.round(ev.wait_minutes || 0)} min wait</span></>
                      : <> · <span className="text-red-400 font-bold">still at stop · {liveMin} min so far</span></>}
                  </div>
                );
              })}
              <div className="text-[10px] text-[#71717A]">Bill waiting time via Pricing → Misc charge (e.g. "Stop wait · 15 min").</div>
            </div>
          )}
          <Input testid="of-dropoff-addr" label="Drop-off" value={form.dropoff_address} onChange={v => upd("dropoff_address", v)}/>
          <Input testid="of-flight" label="Flight Number (optional)" value={form.flight_number} onChange={v => upd("flight_number", v)}/>
        </div>
        {form.flight_number ? (
          <FlightTrackerPanel booking={booking} onChange={onSaved}/>
        ) : (
          <div className="border border-blue-500/30 bg-blue-500/5 p-3 mt-3 flex items-start gap-2">
            <Plane size={14} className="text-blue-300 mt-0.5"/>
            <div className="text-[11px] text-blue-200">
              <b>Flight Tracking Ready.</b> Enter the airline + flight number above for automatic flight delay updates.
            </div>
          </div>
        )}
        <div className="flex gap-4 mt-3 text-[11px] uppercase tracking-[0.18em] text-[#A1A1AA]">
          <span>Distance: <span className="text-[#F5F2EB]">{(booking.distance_miles || 0).toFixed(1)} mi</span></span>
          <span>Duration: <span className="text-[#F5F2EB]">{booking.duration_minutes || 0} min</span></span>
        </div>

        {/* Live route preview — pickup → stops → drop-off. Re-renders if the
            dispatcher edits any address. Same component used by the New
            Reservation modal so behaviour stays consistent. */}
        <RoutePreviewMap
          pickup={form.pickup_address}
          stops={(form.stops || []).filter(s => s && s.trim())}
          dropoff={form.dropoff_address}
        />
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Notes (visible to customer)">
          <textarea data-testid="of-notes" rows={5} value={form.notes} onChange={e => upd("notes", e.target.value)}
            placeholder="Add any special instructions or notes…"
            className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm text-[#F5F2EB] focus:border-[#D4AF37]/60 focus:outline-none"/>
        </Section>
        <Section title="Internal Notes (driver/admin only)">
          <textarea data-testid="of-internal-notes" rows={5} value={form.internal_notes} onChange={e => upd("internal_notes", e.target.value)}
            placeholder="Internal notes for driver/admin use only (not shared with customer)…"
            className="w-full bg-[transparent] border border-[#D4AF37]/30 px-3 py-2 text-sm text-[#F5F2EB] focus:border-[#D4AF37]/60 focus:outline-none"/>
        </Section>
      </div>

      {booking.assigned_driver_id && (
        <AssignedVehiclePanel booking={booking} onSaved={onSaved}/>
      )}

      <div className="flex justify-end gap-3">
        <button data-testid="of-save" onClick={save} disabled={busy} className="btn-gold !px-6"><Save size={14}/> {busy ? "Saving…" : "Save Reservation"}</button>
      </div>

      {/* Live trip chat — only while trip is active */}
      {["assigned", "en_route", "in_progress"].includes(booking.status) && (
        <Section title="Trip chat with driver/customer">
          <TripChat bookingId={booking.booking_id} viewerHint="You · Dispatcher" compact/>
        </Section>
      )}
    </div>
  );
}

// =============================================================
// ASSIGNED VEHICLE OVERRIDE (per-booking — for drivers who own multiple vehicles)
// =============================================================
function AssignedVehiclePanel({ booking, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(null);    // base64 data URL or null = unchanged
  const [photoStatus, setPhotoStatus] = useState(""); // "compressing" | "" | error msg
  const [garage, setGarage] = useState([]);    // driver's saved vehicles (with photos)
  useEffect(() => {
    setForm({
      vehicle_year:  booking.assigned_driver_vehicle_year ?? "",
      vehicle_make:  booking.assigned_driver_vehicle_make ?? "",
      vehicle_model: booking.assigned_driver_vehicle_model ?? "",
      vehicle_color: booking.assigned_driver_vehicle_color ?? "",
      license_plate: booking.assigned_driver_license_plate ?? "",
    });
    setPhoto(null);
    setPhotoStatus("");
    // Load the driver's garage so dispatch can one-click swap the car
    if (booking.assigned_driver_id) {
      api.get(`/drivers/${booking.assigned_driver_id}/vehicles`, { params: { with_photos: true } })
        .then(r => setGarage(r.data || [])).catch(() => setGarage([]));
    } else setGarage([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.booking_id, booking.assigned_driver_id]);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pickFromGarage = (vid) => {
    const v = garage.find(g => g.vid === vid);
    if (!v) return;
    setForm({
      vehicle_year: v.year || "", vehicle_make: v.make || "", vehicle_model: v.model || "",
      vehicle_color: v.color || "", license_plate: v.plate || "",
    });
    setPhoto(v.photo || "");
    toast.info(`Loaded ${[v.year, v.make, v.model].filter(Boolean).join(" ")} — press Save to apply to this trip`);
  };

  const onPickPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setPhotoStatus("Please choose an image file.");
      return;
    }
    setPhotoStatus("compressing");
    try {
      const dataUrl = await compressImage(file, { maxWidth: 720, quality: 0.78 });
      setPhoto(dataUrl);
      setPhotoStatus("");
    } catch (err) {
      console.error(err);
      setPhotoStatus("Could not read that image. Try another.");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        vehicle_year: String(form.vehicle_year || "").trim() || null,
        vehicle_make: (form.vehicle_make || "").trim() || null,
        vehicle_model: (form.vehicle_model || "").trim() || null,
        vehicle_color: (form.vehicle_color || "").trim() || null,
        license_plate: (form.license_plate || "").trim() || null,
      };
      // Only send the photo field if the dispatcher actually changed it
      // (null = "explicit clear", undefined/omitted = "leave as-is")
      if (photo !== null) payload.vehicle_photo = photo === "" ? null : photo;
      await api.patch(`/bookings/${booking.booking_id}/assigned-vehicle`, payload);
      toast.success("Chauffeur vehicle updated — visible on customer Track page");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  const previewParts = [form.vehicle_year, form.vehicle_color, form.vehicle_make, form.vehicle_model].filter(Boolean).join(" ");
  // Existing photo (if any) – served as a real image by GET /api/bookings/{id}/vehicle-photo
  const existingPhotoSrc = booking.assigned_driver_vehicle_photo || null;
  const displayPhotoSrc = photo === "" ? null : (photo || existingPhotoSrc);
  return (
    <Section
      title={`Chauffeur Vehicle · ${booking.assigned_driver_name || "—"}`}
      testid="rd-assigned-vehicle"
      actions={
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">
          Shown to customer on Track page
        </span>
      }
    >
      {garage.length > 0 && (
        <div className="mb-4 flex items-center gap-3 flex-wrap" data-testid="av-garage-picker">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37]">Saved vehicles</span>
          {garage.map(v => (
            <button key={v.vid} data-testid={`av-garage-${v.vid}`} onClick={() => pickFromGarage(v.vid)}
              className="px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] border border-white/15 text-[#F5F2EB] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              {[v.year, v.make, v.model].filter(Boolean).join(" ") || "Vehicle"}{v.is_primary ? " ★" : ""}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Input testid="av-year"  label="Year"  type="number" value={form.vehicle_year}  onChange={v => upd("vehicle_year", v)}/>
        <Input testid="av-make"  label="Make"  value={form.vehicle_make}  onChange={v => upd("vehicle_make", v)}/>
        <Input testid="av-model" label="Model" value={form.vehicle_model} onChange={v => upd("vehicle_model", v)}/>
        <Input testid="av-color" label="Color" value={form.vehicle_color} onChange={v => upd("vehicle_color", v)}/>
        <Input testid="av-plate" label="Plate" value={form.license_plate} onChange={v => upd("license_plate", v)}/>
      </div>

      {/* Optional photo of the actual car — auto-resized in the browser */}
      <div className="mt-5 border border-white/5 bg-black/30 p-4 grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4 items-start">
        <div className="w-40 h-28 border border-white/10 bg-[#0a0a0a] overflow-hidden flex items-center justify-center text-[10px] uppercase tracking-[0.18em] text-[#71717A]">
          {displayPhotoSrc ? (
            <img data-testid="av-photo-preview" src={displayPhotoSrc} alt="Chauffeur vehicle" className="w-full h-full object-cover"/>
          ) : (
            <span>No photo</span>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Vehicle photo (optional)</div>
          <div className="text-[11px] text-[#A1A1AA] leading-relaxed">
            Upload a single photo of the actual car so the customer knows exactly what to look for at pickup.
            We resize to 720px wide JPEG automatically — phone photos are fine.
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <label
              data-testid="av-photo-pick"
              className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10"
            >
              {displayPhotoSrc ? "Replace photo" : "Upload photo"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => onPickPhoto(e.target.files?.[0])}
              />
            </label>
            {displayPhotoSrc && (
              <button
                type="button"
                data-testid="av-photo-clear"
                onClick={() => { setPhoto(""); setPhotoStatus(""); }}
                className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-red-300 border border-red-500/40 hover:bg-red-500/10"
              >
                Remove photo
              </button>
            )}
            {photoStatus === "compressing" && (
              <span className="text-[11px] text-[#A1A1AA]">Compressing…</span>
            )}
            {photoStatus && photoStatus !== "compressing" && (
              <span className="text-[11px] text-red-300">{photoStatus}</span>
            )}
            {photo !== null && photoStatus !== "compressing" && (
              <span className="text-[11px] text-[#D4AF37]">Unsaved photo change · click Save to apply</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-[11px] text-[#A1A1AA]">
          {previewParts || form.license_plate
            ? <>Preview: <span className="text-[#F5F2EB]">{previewParts || "—"}</span>{form.license_plate ? <> · <span className="text-[#D4AF37] font-mono">{form.license_plate}</span></> : null}</>
            : <>Some drivers operate multiple vehicles — override here so the customer sees the exact car arriving.</>
          }
        </div>
        <button data-testid="av-save" onClick={save} disabled={busy} className="btn-gold !py-1.5 !px-4 text-[11px]">
          <Save size={12}/> {busy ? "Saving…" : "Save Vehicle"}
        </button>
      </div>
    </Section>
  );
}

// Resize image client-side via canvas so we never push >100KB across the wire.
async function compressImage(file, { maxWidth = 720, quality = 0.78 } = {}) {
  const buf = await file.arrayBuffer();
  const blob = new Blob([buf], { type: file.type });
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = rej;
      im.src = url;
    });
    const w = Math.min(maxWidth, img.naturalWidth);
    const h = Math.round(img.naturalHeight * (w / img.naturalWidth));
    const cv = document.createElement("canvas");
    cv.width = w; cv.height = h;
    cv.getContext("2d").drawImage(img, 0, 0, w, h);
    return cv.toDataURL("image/jpeg", quality);
  } finally {
    URL.revokeObjectURL(url);
  }
}

// =============================================================
// PRICING PANEL
// =============================================================
/** Dispatcher-only: apply ANY coupon code to this reservation, bypassing the
 *  customer eligibility / season / once-per-day limits. Reprices server-side. */
function CouponOverrideRow({ booking, onSaved }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const current = booking.coupon_code;
  const paid = booking.payment_status === "paid";
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/coupon`, { code: code.trim().toUpperCase() });
      toast.success(`${r.data.coupon_code} applied (${r.data.coupon_pct}% off) — new total $${Number(r.data.fare).toFixed(2)}`);
      setCode("");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Coupon not found"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/bookings/${booking.booking_id}/coupon`);
      toast.success(`Coupon removed — new total $${Number(r.data.fare).toFixed(2)}`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-2.5 py-2 mb-2" data-testid="px-coupon-override">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[9px] uppercase tracking-[0.18em] text-[#D4AF37]">Apply coupon · dispatch override (no customer limits)</span>
        {current && !paid && (
          <button data-testid="px-coupon-remove" onClick={remove} disabled={busy} className="text-[9px] uppercase tracking-[0.15em] text-red-400 hover:underline disabled:opacity-40">Remove {current}</button>
        )}
      </div>
      <div className="flex gap-2">
        <input data-testid="px-coupon-code" placeholder={current ? `Replace ${current}…` : "Any code, e.g. WELCOME15"} value={code}
          onChange={e => setCode(e.target.value.toUpperCase())} onKeyDown={e => e.key === "Enter" && apply()} disabled={paid}
          className="flex-1 bg-transparent border border-white/10 px-2 py-1 text-xs uppercase disabled:opacity-40"/>
        <button data-testid="px-coupon-apply" onClick={apply} disabled={busy || paid || !code.trim()}
          className="text-[10px] uppercase tracking-[0.18em] px-3 py-1 border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-40">
          {busy ? "…" : "Apply"}
        </button>
      </div>
      {paid && <div className="text-[10px] text-[#71717A] mt-1">Reservation is paid — coupon can no longer be changed.</div>}
    </div>
  );
}

function PricingPanel({ booking, onSaved }) {
  const bd = booking.fare_breakdown || {};
  const [base, setBase] = useState(bd.base || 0);
  const [paxPricing, setPaxPricing] = useState(bd.passenger_pricing || 0);
  const [gratuityPct, setGratuityPct] = useState((bd.tip_pct ?? 0.20) * 100);
  const [airport, setAirport] = useState(bd.airport_fee || 0);
  const [tolls, setTolls] = useState(bd.tolls || 0);
  const [cc, setCC] = useState(bd.cc_fee || 0);
  const [flex, setFlex] = useState(bd.cancellation_protection_fee || 0);
  const [taxPct, setTaxPct] = useState((bd.tax_pct ?? 0.055) * 100);
  const [promoCode, setPromoCode] = useState(bd.promo_code || "");
  const [promoDisc, setPromoDisc] = useState(bd.promo_discount || 0);
  const [manualDisc, setManualDisc] = useState(bd.manual_discount || 0);
  const [misc, setMisc] = useState(bd.misc_charges || []);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  // Pickup wait-time charge — auto-suggested when the driver's wait timer
  // runs past the free grace window (locked by "passenger aboard" or live).
  const [waitCharge, setWaitCharge] = useState(bd.wait_charge || 0);
  const [waitStatus, setWaitStatus] = useState(null);
  // Editable Total — when the dispatcher types a target total, the backend
  // back-solves the Base Fare so gratuity + tax recompute and the final
  // Total lands exactly on the typed amount. null = not editing.
  const [totalEdit, setTotalEdit] = useState(null);
  // Live system-suggested base fare for the current route + vehicle.
  // Shown as a hint under the Base Fare input so dispatchers can see when
  // they've manually diverged from what the pricing engine would charge.
  const [suggestedBase, setSuggestedBase] = useState(null);

  // Recompute the suggestion whenever the route or vehicle changes.
  useEffect(() => {
    const pickup = booking.pickup_address;
    const dropoff = booking.dropoff_address;
    const vid = booking.vehicle_id;
    if (!pickup || !dropoff || !vid) { setSuggestedBase(null); return; }
    let cancelled = false;
    api.post("/quote", {
      trip_type: booking.trip_type || "point_to_point",
      pickup_address: pickup,
      dropoff_address: dropoff,
      hours: booking.hours || null,
      pax: booking.pax || 1,
      luggage: booking.luggage || 0,
      tip_pct: 0,
    }).then(r => {
      if (cancelled) return;
      const v = r.data.vehicles.find(x => x.vehicle_id === vid);
      if (v?.breakdown) {
        const bf = Number(v.breakdown.base_fare || 0);
        const dc = Number(v.breakdown.distance_cost || 0);
        setSuggestedBase(Number((bf + dc).toFixed(2)));
      }
    }).catch(() => setSuggestedBase(null));
    return () => { cancelled = true; };
  }, [booking.pickup_address, booking.dropoff_address, booking.vehicle_id, booking.pax, booking.trip_type, booking.hours, booking.luggage]);

  useEffect(() => {
    setBase(bd.base || 0);
    setPaxPricing(bd.passenger_pricing || 0);
    setGratuityPct((bd.tip_pct ?? 0.20) * 100);
    setAirport(bd.airport_fee || 0);
    setTolls(bd.tolls || 0);
    setCC(bd.cc_fee || 0);
    setFlex(bd.cancellation_protection_fee || 0);
    setTaxPct((bd.tax_pct ?? 0.055) * 100);
    setPromoCode(bd.promo_code || "");
    setPromoDisc(bd.promo_discount || 0);
    setManualDisc(bd.manual_discount || 0);
    setMisc(bd.misc_charges || []);
    setWaitCharge(bd.wait_charge || 0);
  }, [booking.booking_id]); // eslint-disable-line

  // Live wait-timer status → powers the auto-suggested wait charge banner.
  useEffect(() => {
    let cancelled = false;
    api.get(`/bookings/${booking.booking_id}/wait-status`)
      .then(r => { if (!cancelled) setWaitStatus(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [booking.booking_id]);

  const payload = () => ({
    base_fare: Number(base),
    passenger_pricing: Number(paxPricing),
    gratuity_pct: Number(gratuityPct) / 100,
    airport_fee: Number(airport),
    tolls: Number(tolls),
    credit_card_fee: Number(cc),
    flexible_cancellation: Number(flex),
    tax_pct: Number(taxPct) / 100,
    promo_code: promoCode || null,
    promo_discount: Number(promoDisc),
    manual_discount: Number(manualDisc),
    misc_charges: misc.map(m => ({ label: m.label, amount: Number(m.amount) })),
    wait_charge: Number(waitCharge) || 0,
  });

  // "Modernize line items" — for old-engine bookings where Base Fare was the
  // pickup minimum and the mileage portion lived hidden as `distance_cost`.
  // Sends `modernize_line_items: true` so the backend zeroes the legacy
  // distance_cost before applying the new Base Fare (preventing double-count).
  // Two flavours of Modernize:
  //   • mode="apply"   → write the new engine's prices (Total may shift).
  //   • mode="honour"  → tidy line items but lock Total at the original quote
  //                      (auto-applies a manual_discount to balance).
  const modernize = async (mode = "apply") => {
    if (suggestedBase == null) {
      toast.error("No system suggestion available yet — try again in a moment");
      return;
    }
    setBusy(true);
    try {
      const newBase = Number(suggestedBase);
      const oldTotal = Number(booking.fare || 0);
      const newPayload = {
        ...payload(),
        base_fare: newBase,
        modernize_line_items: true,
        ...(mode === "honour" ? { honour_original_total: oldTotal } : {}),
      };
      const r = await api.post(`/bookings/${booking.booking_id}/pricing`, { ...newPayload, recalculate_only: true });
      const newBd = r.data.fare_breakdown;
      const newTotal = Number(newBd.total || 0);
      const delta = newTotal - oldTotal;
      const sign = delta >= 0 ? "+" : "−";
      const headline = mode === "honour"
        ? `Tidy line items & keep original total?\n\n` +
          `Base Fare:   $${Number(base).toFixed(2)}  →  $${newBase.toFixed(2)}\n` +
          `Manual Discount applied: $${Number(newBd.manual_discount || 0).toFixed(2)}\n` +
          `Total stays at $${newTotal.toFixed(2)}  ✓\n\n` +
          `Customer pays exactly what they were originally quoted.`
        : `Modernize line items?\n\n` +
          `Base Fare:   $${Number(base).toFixed(2)}  →  $${newBase.toFixed(2)}\n` +
          `New Total:   $${newTotal.toFixed(2)}  (was $${oldTotal.toFixed(2)}, ${sign}$${Math.abs(delta).toFixed(2)})\n\n` +
          (Math.abs(delta) > 1
            ? `⚠️  The customer's total WILL change by ${sign}$${Math.abs(delta).toFixed(2)}.\n` +
              `Click OK to save the new amount, or Cancel to leave the booking as-is.`
            : `Line items tidied without changing the total. Safe to save.`);
      if (!window.confirm(headline)) { setBusy(false); return; }
      setBase(newBase);
      setPreview(newBd);
      await api.post(`/bookings/${booking.booking_id}/pricing`, newPayload);
      toast.success(mode === "honour" ? "Tidied · original total kept" : "Line items modernized · pricing saved");
      setPreview(null);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Modernize failed");
    } finally { setBusy(false); }
  };

  // One-tap toll add — for unexpected toll roads taken during the trip.
  // Prompts for the toll amount, adds it to the Tolls line and saves in one shot.
  const quickAddToll = async () => {
    const raw = window.prompt("Toll amount to ADD ($):", "");
    if (raw === null) return;
    const amt = Number(raw);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Enter a valid toll amount"); return; }
    const newTolls = Number((Number(tolls || 0) + amt).toFixed(2));
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/pricing`, { ...payload(), tolls: newTolls });
      setTolls(newTolls);
      toast.success(`Toll added — Tolls now $${newTolls.toFixed(2)}`);
      setPreview(null);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toll add failed"); }
    finally { setBusy(false); }
  };

  const recalc = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/pricing`, { ...payload(), recalculate_only: true });
      setPreview(r.data.fare_breakdown);
      toast.success("Recalculated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Recalc failed"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    const oldFare = Number(booking.fare || 0);
    const newFare = Number(total || 0);
    // Safety net — if this save would drop the customer's total, require confirmation.
    if (oldFare > 0 && newFare + 0.01 < oldFare) {
      const drop = (oldFare - newFare).toFixed(2);
      const ok = window.confirm(
        `⚠️ This will LOWER the total by $${drop}\n\n` +
        `  Current total (customer sees): $${oldFare.toFixed(2)}\n` +
        `  New total after save:          $${newFare.toFixed(2)}\n\n` +
        `The customer will see the new lower amount on their receipt and next confirmation. Continue?`
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/pricing`, payload());
      const bal = r.data?.balance;
      if (bal?.payment_status === "balance_due") toast.warning(`Pricing saved — trip was PAID; new balance due $${(bal.balance_due_cents / 100).toFixed(2)}. Use Charge Balance.`);
      else toast.success("Pricing saved");
      setPreview(null);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  // Dispatcher typed a new Total — ask the backend to back-solve the Base
  // Fare so gratuity/tax recompute and the total lands exactly on target.
  const applyTargetTotal = async () => {
    if (totalEdit === null) return;
    const target = Number(totalEdit);
    setTotalEdit(null);
    if (!Number.isFinite(target) || target <= 0 || Math.abs(target - Number(total)) < 0.005) return;
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/pricing`, { ...payload(), target_total: target, recalculate_only: true });
      const nb = r.data.fare_breakdown;
      setBase(nb.base ?? 0);
      setPreview(nb);
      toast.success(`Base Fare auto-adjusted to $${Number(nb.base || 0).toFixed(2)} · Total $${Number(nb.total || 0).toFixed(2)} — click Save Pricing to keep it`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't match that total");
    } finally { setBusy(false); }
  };

  // Apply the auto-suggested wait charge (recomputes preview with it included)
  const applyWaitSuggestion = async () => {
    const sug = Number(waitStatus?.charge || 0);
    if (sug <= 0) return;
    setWaitCharge(sug);
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/pricing`,
        { ...payload(), wait_charge: sug, recalculate_only: true });
      setPreview(r.data.fare_breakdown);
      toast.success(`Wait charge $${sug.toFixed(2)} applied — click Save Pricing to keep it`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply wait charge"); }
    finally { setBusy(false); }
  };

  const addMisc = () => setMisc(arr => [...arr, { label: "Misc", amount: 0 }]);
  const removeMisc = (i) => setMisc(arr => arr.filter((_, idx) => idx !== i));
  const updMisc = (i, k, v) => setMisc(arr => arr.map((m, idx) => idx === i ? { ...m, [k]: v } : m));

  const view = preview || bd;
  const subtotal = view.subtotal || 0;
  const total = view.total || 0;

  // Auto-suggest a wait charge when the wait timer ran past the free grace
  // window and the current charge is below the computed suggestion. Only for
  // trustworthy timers: a locked aboard-snapshot, or a live trip where the
  // driver is on location / in progress.
  const waitSuggestion = Boolean(
    waitStatus && !waitStatus.error
    && Number(waitStatus.billable_minutes || 0) > 0
    && Number(waitStatus.charge || 0) > Number(waitCharge || 0) + 0.005
    && (waitStatus.locked || ["on_location", "in_progress"].includes(booking.status))
  );

  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5" data-testid="rd-pricing-panel">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-serif text-lg">Pricing</h3>
        <div className="flex gap-1 flex-wrap justify-end">
          {/* Modernize — shows only when the stored Base Fare differs ≥ $1
              from the new-engine suggestion. Two flavours: "apply" writes the
              new engine's total (may shift); "honour" keeps the original total
              and tidies the line items via an auto-discount. */}
          {suggestedBase != null && Math.abs(Number(base) - suggestedBase) >= 1 && (
            <>
              <button
                data-testid="px-modernize"
                onClick={() => modernize("apply")}
                disabled={busy}
                title="Rewrite Base Fare = base + miles (new engine) and save the new total"
                className="text-[10px] uppercase tracking-[0.18em] bg-[#D4AF37] text-[#0A0A0A] font-bold px-2 py-1 border border-[#D4AF37] hover:bg-[#C29B2E] disabled:opacity-50"
              >✨ Modernize</button>
              <button
                data-testid="px-modernize-honour"
                onClick={() => modernize("honour")}
                disabled={busy}
                title="Tidy line items but keep the original quoted total via auto-discount"
                className="text-[10px] uppercase tracking-[0.18em] bg-emerald-500/20 text-emerald-300 px-2 py-1 border border-emerald-500/40 hover:bg-emerald-500/30 disabled:opacity-50"
              >🧹 Tidy · keep ${Number(booking.fare || 0).toFixed(0)}</button>
            </>
          )}
          <button data-testid="px-recalc" onClick={recalc} disabled={busy} className="text-[10px] uppercase tracking-[0.18em] bg-blue-500/20 text-blue-300 px-2 py-1 border border-blue-500/30">{busy ? "…" : "Recalculate"}</button>
          <button data-testid="px-add-misc" onClick={addMisc} className="text-[10px] uppercase tracking-[0.18em] bg-amber-500/20 text-amber-300 px-2 py-1 border border-amber-500/30">+ Misc</button>
        </div>
      </div>

      <PriceRow testid="px-base" label="Base Fare" value={base} onChange={setBase}
                suggested={suggestedBase}
                storedTotal={booking.fare}
                onResetSuggested={() => setBase(suggestedBase)}/>
      <PriceRow testid="px-pax" label={`Passenger Pricing · ${booking.pax || 1} pax`} value={paxPricing} onChange={setPaxPricing}/>

      <SectionLabel>Gratuity</SectionLabel>
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs text-[#A1A1AA]">Gratuity ({gratuityPct.toFixed(0)}%)</span>
        <select data-testid="px-tip-select" value={gratuityPct} onChange={e => setGratuityPct(Number(e.target.value))}
          className="bg-transparent border border-white/10 px-1.5 py-0.5 text-xs">
          {[0, 10, 15, 18, 20, 25, 30].map(p => <option key={p} value={p}>{p}%</option>)}
        </select>
      </div>
      <div className="flex justify-between text-sm border-b border-white/5 py-1">
        <span className="text-[#A1A1AA]">Gratuity</span>
        <span className="text-[#F5F2EB]">${((view.gratuity ?? (subtotal * (gratuityPct / 100))) || 0).toFixed(2)}</span>
      </div>

      <SectionLabel>Custom Charges</SectionLabel>
      <PriceRow testid="px-airport" label="Airport Tax" value={airport} onChange={setAirport}/>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <PriceRow testid="px-tolls" label="Tolls" value={tolls} onChange={setTolls}/>
        </div>
        <button data-testid="px-quick-add-toll" disabled={busy} onClick={quickAddToll}
          title="Add an unexpected toll (e.g. detour via toll road) and save in one tap"
          className="text-[10px] uppercase tracking-[0.14em] text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 px-2 py-1 shrink-0">
          + Add toll
        </button>
      </div>
      <PriceRow testid="px-cc" label="Credit Card Fee" value={cc} onChange={setCC}/>
      <PriceRow testid="px-flex" label="Flexible Cancellation" value={flex} onChange={setFlex}/>

      {misc.length > 0 && (
        <>
          <SectionLabel>Misc</SectionLabel>
          {misc.map((m, i) => (
            <div key={i} className="grid grid-cols-12 gap-1 mb-1.5 items-center">
              <input data-testid={`px-misc-label-${i}`} value={m.label} onChange={e => updMisc(i, "label", e.target.value)} className="col-span-6 bg-transparent border border-white/10 px-2 py-1 text-xs text-[#F5F2EB]"/>
              <div className="col-span-5 flex items-center"><span className="text-xs text-[#71717A] pr-1">$</span>
                <input data-testid={`px-misc-amount-${i}`} type="number" step="0.01" value={m.amount} onChange={e => updMisc(i, "amount", e.target.value)} className="flex-1 bg-transparent border border-white/10 px-2 py-1 text-xs text-[#F5F2EB] text-right"/>
              </div>
              <button onClick={() => removeMisc(i)} className="col-span-1 text-red-400 text-xs">×</button>
            </div>
          ))}
        </>
      )}

      {Number(view.event_surcharge || 0) !== 0 && (
        <div className="flex justify-between text-sm border-b border-white/5 py-1" data-testid="px-event-surcharge-line">
          <span className="text-[#D4AF37]">{view.event_surcharge_label || "Scheduled rate"} ({view.event_surcharge_pct > 0 ? "+" : ""}{view.event_surcharge_pct}%)</span>
          <span className="text-[#D4AF37]">{view.event_surcharge < 0 ? "−" : "+"}${Math.abs(Number(view.event_surcharge)).toFixed(2)}</span>
        </div>
      )}
      <div className="flex justify-between font-medium pt-2 border-t border-white/10 mt-2">
        <span className="text-[#A1A1AA]">Subtotal</span>
        <span className="text-[#F5F2EB]" data-testid="px-subtotal">${subtotal.toFixed(2)}</span>
      </div>
      {Number(view.coupon_discount || 0) > 0 && (
        <div className="flex justify-between text-sm border-b border-white/5 py-1" data-testid="px-coupon-line">
          <span className="text-emerald-400">Coupon{view.coupon_code ? ` · ${view.coupon_code}` : ""}{view.coupon_pct ? ` (${Number(view.coupon_pct).toFixed(0)}% off)` : ""}</span>
          <span className="text-emerald-400">−${Number(view.coupon_discount).toFixed(2)}</span>
        </div>
      )}

      <SectionLabel>Discounts</SectionLabel>
      <CouponOverrideRow booking={booking} onSaved={onSaved}/>
      <div className="grid grid-cols-2 gap-2 mb-1.5">
        <input data-testid="px-promo-code" placeholder="Promo code" value={promoCode} onChange={e => setPromoCode(e.target.value)} className="bg-transparent border border-white/10 px-2 py-1 text-xs"/>
        <div className="flex items-center"><span className="text-xs text-[#71717A] pr-1">-$</span>
          <input data-testid="px-promo-disc" type="number" step="0.01" value={promoDisc} onChange={e => setPromoDisc(e.target.value)} className="flex-1 bg-transparent border border-white/10 px-2 py-1 text-xs text-right"/>
        </div>
      </div>
      <div className="flex justify-between text-sm border-b border-white/5 py-1">
        <span className="text-[#A1A1AA]">Manual Discount</span>
        <div className="flex items-center"><span className="text-xs text-[#71717A] pr-1">-$</span>
          <input data-testid="px-manual-disc" type="number" step="0.01" value={manualDisc} onChange={e => setManualDisc(e.target.value)} className="w-24 bg-transparent border border-white/10 px-2 py-1 text-xs text-right"/>
        </div>
      </div>

      <SectionLabel>Tax Breakdown</SectionLabel>
      <div className="flex justify-between text-sm border-b border-white/5 py-1">
        <span className="text-[#A1A1AA]">State Sales Tax ({taxPct.toFixed(1)}%)</span>
        <div className="flex items-center gap-1">
          <input data-testid="px-tax-pct" type="number" step="0.1" value={taxPct} onChange={e => setTaxPct(e.target.value)} className="w-12 bg-transparent border border-white/10 px-1 py-0.5 text-xs text-right"/>
          <span className="text-[#F5F2EB] w-20 text-right">${(view.tax || 0).toFixed(2)}</span>
        </div>
      </div>

      {/* ── Pickup wait-time charge ── */}
      {waitSuggestion && (
        <div data-testid="px-wait-suggestion" className="border border-amber-500/40 bg-amber-500/10 p-3 mt-2 flex items-center justify-between gap-2">
          <div className="text-xs text-amber-200">
            ⏱ Wait timer: <b>{waitStatus.billable_minutes} min</b> past the {waitStatus.free_minutes}-min free window
            — suggested charge <b>${Number(waitStatus.charge).toFixed(2)}</b>
          </div>
          <button data-testid="px-wait-apply" onClick={applyWaitSuggestion} disabled={busy}
                  className="text-[10px] uppercase tracking-[0.18em] bg-amber-500/20 text-amber-300 px-2 py-1 border border-amber-500/40 hover:bg-amber-500/30 disabled:opacity-50 whitespace-nowrap">
            Apply
          </button>
        </div>
      )}
      {(Number(waitCharge) > 0 || Number(view.wait_charge || 0) > 0) && (
        <div className="flex justify-between text-sm border-b border-white/5 py-1" data-testid="px-wait-row">
          <span className="text-[#A1A1AA]">Pickup wait{view.wait_billable_minutes ? ` · ${Math.round(view.wait_billable_minutes)} min` : ""}</span>
          <div className="flex items-center">
            <span className="text-xs text-[#71717A] pr-1">$</span>
            <input data-testid="px-wait-charge" type="number" step="0.01" min="0" value={waitCharge}
                   onChange={e => setWaitCharge(e.target.value)}
                   className="w-24 bg-transparent border border-white/10 px-2 py-1 text-xs text-right"/>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center text-lg font-serif pt-3 border-t border-[#D4AF37]/30 mt-3">
        <span>Total</span>
        <div className="flex items-center gap-1" data-testid="px-total">
          <span className="text-[#D4AF37]">$</span>
          <input
            data-testid="px-total-input"
            type="number"
            step="0.01"
            min="0"
            value={totalEdit ?? total.toFixed(2)}
            onFocus={() => setTotalEdit(total.toFixed(2))}
            onChange={e => setTotalEdit(e.target.value)}
            onBlur={applyTargetTotal}
            onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
            disabled={busy}
            className="w-28 bg-transparent border border-[#D4AF37]/40 focus:border-[#D4AF37] px-2 py-1 text-right text-lg font-serif text-[#D4AF37] outline-none disabled:opacity-50"
            title="Type a new total — Base Fare auto-adjusts so gratuity & tax recompute to land exactly on it"
          />
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#71717A] mt-1 text-right">Type a new total &amp; press Enter — Base Fare auto-adjusts</div>
      <BalanceDueBanner booking={booking} onChanged={onSaved} compact/>

      {preview && <div className="text-[10px] uppercase tracking-[0.18em] text-amber-400 mt-2">Preview (not saved yet)</div>}

      <button data-testid="px-save" onClick={save} disabled={busy} className="btn-gold w-full justify-center mt-4">{busy ? "Saving…" : "💾 Save Pricing"}</button>
    </div>
  );
}

function PriceRow({ testid, label, value, onChange, suggested, onResetSuggested, storedTotal }) {
  // Highlight when the dispatcher's manual value has drifted >1¢ from the
  // pricing engine's suggestion. Lets them see at a glance that the system
  // would otherwise charge a different base fare.
  const numericValue = Number(value) || 0;
  const hasSuggestion = suggested != null && !isNaN(suggested);
  const diverged = hasSuggestion && Math.abs(numericValue - suggested) > 0.01;
  // A legacy booking is one where the line item is empty ($0) but the booking's
  // stored total is > 0 — meaning the old engine packed cost into a different
  // field. In that case, "you're $X below" is misleading (the customer already
  // paid the correct total). Show a neutral legacy hint instead.
  const isLegacyLineItem = numericValue < 0.01 && Number(storedTotal || 0) > 0.01;
  return (
    <div className="border-b border-white/5 py-1.5">
      <div className="flex justify-between items-center text-sm">
        <span className="text-[#A1A1AA] flex-1">{label}</span>
        <div className="flex items-center gap-0.5">
          <span className="text-xs text-[#71717A]">$</span>
          <input data-testid={testid} type="number" step="0.01" value={value} onChange={e => onChange(e.target.value)}
            className={`w-24 bg-transparent border px-2 py-1 text-xs text-right ${diverged && !isLegacyLineItem ? "border-amber-500/60 text-amber-300" : "border-white/10 text-[#F5F2EB]"}`}/>
        </div>
      </div>
      {hasSuggestion && (
        <div className="flex justify-end items-center gap-2 mt-0.5">
          {isLegacyLineItem ? (
            <span className="text-[10px] text-[#71717A]/80">
              New engine would price this line at <span className="font-medium text-[#A1A1AA]">${suggested.toFixed(2)}</span> — total ${Number(storedTotal).toFixed(2)} is already correct. Use MODERNIZE to migrate.
            </span>
          ) : (
            <>
              <span className={`text-[10px] ${diverged ? "text-amber-300/80" : "text-emerald-400/60"}`}>
                System suggests <span className="font-medium">${suggested.toFixed(2)}</span>
                {diverged && (numericValue < suggested ? ` (you’re $${(suggested - numericValue).toFixed(2)} below)` : ` (you’re $${(numericValue - suggested).toFixed(2)} above)`)}
              </span>
              {diverged && onResetSuggested && (
                <button
                  type="button"
                  data-testid={`${testid}-reset`}
                  onClick={onResetSuggested}
                  className="text-[9px] uppercase tracking-[0.16em] text-[#D4AF37] hover:text-[#C29B2E] underline-offset-2 hover:underline"
                >Use ${suggested.toFixed(2)}</button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
function SectionLabel({ children }) {
  return <div className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37] mt-3 mb-1.5">{children}</div>;
}

// =============================================================
// PAYMENTS TAB (v2 — full reservation payments console)
// =============================================================
function PaymentsTab({ bookingId, booking, onBookingChanged }) {
  const [payments, setPayments] = useState([]);
  const [cards, setCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState("");
  const [busy, setBusy] = useState(false);
  const [showOutside, setShowOutside] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [showChargeCard, setShowChargeCard] = useState(false);
  const [showCopyCard, setShowCopyCard] = useState(false);
  const [showChargeRemaining, setShowChargeRemaining] = useState(false);
  // Dispatcher Payment Override — manual amount (dollars) for the charge buttons.
  // Defaults to the outstanding remainder; falls back to the full fare.
  const [chargeAmount, setChargeAmount] = useState("");
  const [chargeReason, setChargeReason] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        api.get(`/bookings/${bookingId}/payments`),
        api.get(`/payments/cards/by-email`, { params: { email: booking.customer_email } }).catch(() => ({ data: [] })),
      ]);
      setPayments(p.data || []);
      setCards(c.data || []);
      if ((c.data || []).length && !selectedCardId) setSelectedCardId(c.data[0].saved_card_id);
    } catch (e) { /* noop */ }
  }, [bookingId, booking.customer_email, selectedCardId]);

  useEffect(() => { refresh(); }, [refresh]);

  const completedPayments = payments.filter(p => p.status === "paid" && (p.amount_cents || 0) > 0);
  const refundedPayments = payments.filter(p => p.status === "refunded");
  const totalPaidCents = completedPayments.reduce((a, p) => a + (p.amount_cents || 0), 0)
                     + refundedPayments.reduce((a, p) => a + (p.amount_cents || 0), 0); // refunds are negative
  const fareCents = Math.round((booking.fare || 0) * 100);
  const overpayCents = Math.max(0, totalPaidCents - fareCents);
  // Outstanding = fare − what the balance engine says was collected toward the fare
  // (falls back to the ledger sum for legacy trips).
  const collectedCents = booking.paid_toward_fare_cents ?? (booking.payment_status === "paid" ? fareCents : totalPaidCents);
  const pendingCents = Math.max(0, fareCents - collectedCents);
  const defaultChargeCents = pendingCents > 0 ? pendingCents : fareCents;
  useEffect(() => { setChargeAmount((defaultChargeCents / 100).toFixed(2)); }, [defaultChargeCents]);
  const chargeCents = Math.round((Number(chargeAmount) || 0) * 100);
  const isOverrideAmount = chargeCents !== fareCents || booking.payment_status !== "pay_later" || collectedCents > 0;

  const cents = (c) => `$${(c / 100).toFixed(2)}`;
  const selectedCard = cards.find(c => c.saved_card_id === selectedCardId);

  // Actions
  const processPayment = async () => {
    if (!selectedCardId) { toast.error("Select a card first"); return; }
    if (chargeCents <= 0) { toast.error("Enter an amount to charge"); return; }
    const extra = chargeCents > pendingCents && pendingCents > 0 ? `\n\n⚠ This is more than the ${cents(pendingCents)} outstanding.`
      : pendingCents === 0 ? `\n\n⚠ Nothing is outstanding on this trip — this is an additional charge.` : "";
    if (!window.confirm(`Charge ${cents(chargeCents)} to card ending ${selectedCard?.last4}?${extra}`)) return;
    setBusy(true);
    try {
      const body = { booking_id: bookingId, saved_card_id: selectedCardId };
      if (isOverrideAmount) { body.amount = chargeCents / 100; body.reason = chargeReason || (pendingCents > 0 ? "Remaining balance" : "Dispatcher charge"); }
      const r = await api.post("/payments/charge-saved", body);
      toast.success(`${cents(r.data.amount_cents)} captured · ${r.data.payment_id?.slice(0, 10)}…${r.data.fully_paid === false ? ` · ${cents(r.data.balance_due_cents)} still outstanding` : ""}`);
      refresh(); onBookingChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Charge failed"); }
    finally { setBusy(false); }
  };
  const overrideStatus = async (status) => {
    const label = { paid: "PAID", balance_due: "PARTIALLY PAID", pay_later: "UNPAID" }[status];
    let paid_amount;
    if (status === "balance_due") {
      const v = window.prompt(`Set as PARTIALLY PAID — how much has been collected so far? (fare ${cents(fareCents)})`, (collectedCents / 100).toFixed(2));
      if (v === null) return;
      paid_amount = Number(v);
      if (!Number.isFinite(paid_amount) || paid_amount < 0) { toast.error("Invalid amount"); return; }
    } else if (!window.confirm(`Manually mark this reservation ${label}? No card will be charged — this only changes the status (logged for audit).`)) return;
    const reason = window.prompt("Reason (optional, for the audit log)", "") ?? "";
    setBusy(true);
    try {
      await api.post("/payments/status-override", { booking_id: bookingId, status, paid_amount, reason: reason || null });
      toast.success(`Status set to ${label}`);
      refresh(); onBookingChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Override failed"); }
    finally { setBusy(false); }
  };
  const unmarkInvoice = async () => {
    if (!window.confirm("Unmark this invoice? Booking will reset to pay-later.")) return;
    setBusy(true);
    try {
      await api.post(`/payments/booking/${bookingId}/unmark`, { reason: "dispatcher_unmark" });
      toast.success("Invoice unmarked");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const sendReceipt = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${bookingId}/email-receipt`);
      toast.success(r.data.status === "MOCKED" ? "Receipt queued (email mocked)" : "Receipt emailed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const deleteCard = async () => {
    if (!selectedCard) return;
    if (!window.confirm(`Delete card ····${selectedCard.last4}?`)) return;
    try {
      await api.delete(`/payments/cards/${selectedCard.saved_card_id}`);
      toast.success("Card deleted");
      setSelectedCardId("");
      refresh();
    } catch (e) { toast.error("Delete failed"); }
  };
  // Inline-edit a manual / outside payment-log entry. Lets the dispatcher
  // correct a typo (e.g. $150 → $130) without the delete+re-add dance.
  const [editingLogId, setEditingLogId] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editMethod, setEditMethod] = useState("");
  const beginEdit = (p) => {
    setEditingLogId(p.log_id);
    setEditAmount(((p.amount_cents || 0) / 100).toFixed(2));
    setEditMethod(p.method || "");
  };
  const cancelEdit = () => { setEditingLogId(null); setEditAmount(""); setEditMethod(""); };
  const saveEdit = async (p) => {
    try {
      await api.patch(`/bookings/${bookingId}/payments/${p.log_id}`, {
        amount: Number(editAmount),
        method: editMethod.trim().toLowerCase() || undefined,
      });
      toast.success("Payment entry updated");
      cancelEdit();
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const refundPayment = async (p) => {
    if (!p.payment_id) { toast.error("This payment has no Square reference (cannot auto-refund)"); return; }
    const fullCents = p.amount_cents || 0;
    const fullDollars = (fullCents / 100).toFixed(2);
    const input = window.prompt(
      `Refund amount for this $${fullDollars} payment?\n\n` +
      `Leave blank or enter $${fullDollars} for a full refund.\n` +
      `Enter a smaller amount for a partial refund (e.g. 20.00).`,
      fullDollars
    );
    if (input === null) return; // cancelled
    const amount = Number(String(input).trim());
    if (!isFinite(amount) || amount <= 0) {
      toast.error("Invalid refund amount");
      return;
    }
    if (amount > fullDollars * 1 + 0.001) {
      toast.error(`Cannot refund more than the original $${fullDollars}`);
      return;
    }
    const isPartial = Math.abs(amount - fullDollars) > 0.01;
    if (!window.confirm(
      `Issue ${isPartial ? "PARTIAL" : "FULL"} refund of $${amount.toFixed(2)}` +
      (isPartial ? ` (of $${fullDollars} original)` : "") + `?`
    )) return;
    try {
      const body = { reason: isPartial ? `Partial refund · $${amount.toFixed(2)}` : "Dispatcher refund" };
      if (isPartial) body.amount = amount;
      const r = await api.post(`/payments/${p.payment_id}/refund`, body);
      toast.success(`Refund ${r.data.status} · ${cents(r.data.amount_cents)}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
  };

  // Delete a single payment-log entry — only allowed for manual / outside
  // entries (Square paid charges must be refunded first). Used when the
  // dispatcher needs to consolidate two transactions into one (e.g. recorded
  // a $150 Venmo + tip-adjust entry → delete both, re-enter as one $150).
  const deletePaymentLog = async (p) => {
    if (!p.log_id) { toast.error("This entry can't be deleted (no log id)"); return; }
    const isSquarePaid = p.status === "paid" && (p.provider === "square" || p.provider === "square_saved") && p.payment_id;
    if (isSquarePaid) {
      toast.error("Refund this Square charge first, then delete it");
      return;
    }
    if (!window.confirm(
      `Delete this payment entry?\n\n` +
      `Amount: ${cents(p.amount_cents)} · Method: ${p.method || "—"} · Status: ${p.status || "—"}\n\n` +
      `This only removes it from the log — does NOT issue a refund. If the customer was actually charged, refund them outside the app first.`
    )) return;
    try {
      const r = await api.delete(`/bookings/${bookingId}/payments/${p.log_id}`);
      if (r.data.remaining_paid_entries === 0) {
        toast.success("Entry deleted · booking flipped back to Pay Later");
      } else {
        toast.success("Entry deleted");
      }
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="rd-payments-v2">
      <div className="flex justify-end gap-2">
        <button data-testid="px-send-receipt" onClick={sendReceipt}
          disabled={busy || (booking.payment_status !== "paid" && totalPaidCents <= 0)}
          title={(booking.payment_status !== "paid" && totalPaidCents <= 0) ? "No payment recorded yet" : "Email the receipt PDF to the customer"}
          className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] bg-blue-600/20 text-blue-300 border border-blue-500/40 disabled:opacity-50">
          📤 Send Payment Receipt
        </button>
        <button onClick={refresh} className="btn-ghost !py-1.5 !px-3 text-[10px]"><RefreshCcw size={12}/> Refresh</button>
      </div>

      <Section title="Payment History">
        {/* Dispatcher Payment Override — amount + always-active charge buttons */}
        <div className="border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] p-3 mb-4" data-testid="px-override-bar">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Amount to charge</span>
              <div className="flex items-center gap-1">
                <span className="text-[#D4AF37]">$</span>
                <input data-testid="px-amount" type="number" step="0.01" min="0" value={chargeAmount} onChange={e => setChargeAmount(e.target.value)}
                  className="w-28 bg-black border border-[#D4AF37]/40 focus:border-[#D4AF37] px-2 py-1.5 text-sm text-[#F5F2EB] outline-none"/>
                {pendingCents > 0 && chargeCents !== pendingCents && (
                  <button type="button" data-testid="px-amount-remaining" onClick={() => setChargeAmount((pendingCents / 100).toFixed(2))} className="text-[9px] uppercase tracking-[0.15em] text-[#D4AF37] underline-offset-2 hover:underline ml-1">Remaining {cents(pendingCents)}</button>
                )}
                {chargeCents !== fareCents && (
                  <button type="button" data-testid="px-amount-full" onClick={() => setChargeAmount((fareCents / 100).toFixed(2))} className="text-[9px] uppercase tracking-[0.15em] text-[#A1A1AA] underline-offset-2 hover:underline ml-1">Full {cents(fareCents)}</button>
                )}
              </div>
            </label>
            <label className="block flex-1 min-w-[180px]">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Reason (on statement &amp; ledger)</span>
              <input data-testid="px-reason" value={chargeReason} onChange={e => setChargeReason(e.target.value)} maxLength={80}
                placeholder={pendingCents > 0 ? "Remaining balance" : "e.g. Extra stop, Deposit, Damage fee"}
                className="w-full bg-black border border-white/15 px-2 py-1.5 text-sm text-[#F5F2EB]"/>
            </label>
            <button data-testid="px-process" onClick={processPayment} disabled={!selectedCardId || busy}
              title={!selectedCardId ? "Select a card on file first" : `Charge ${cents(chargeCents)} to the selected card on file`}
              className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37]/60 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 disabled:opacity-40">$ Charge Card on File {cents(chargeCents)}</button>
            <button data-testid="px-charge-new-card" onClick={() => setShowChargeCard(true)} disabled={busy}
              title={`Enter a new card and charge ${cents(chargeCents)}`}
              className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37]/60 bg-[#D4AF37]/5 hover:bg-[#D4AF37]/15 disabled:opacity-40">💳 Charge New Card</button>
            {pendingCents > 0 && (
              <button data-testid="px-charge-remaining" onClick={() => setShowChargeRemaining(true)} disabled={busy}
                className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-amber-300 border border-amber-500/60 bg-amber-500/10 hover:bg-amber-500/20 disabled:opacity-40">⚡ Charge Remaining {cents(pendingCents)}</button>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] text-[#71717A]">
            <span className="uppercase tracking-[0.18em]">Status override</span>
            {[["paid", "Paid", "text-emerald-300 border-emerald-500/40"], ["balance_due", "Partially paid", "text-amber-300 border-amber-500/40"], ["pay_later", "Unpaid", "text-red-300 border-red-500/40"]].map(([s, l, cls]) => (
              <button key={s} type="button" data-testid={`px-status-${s}`} onClick={() => overrideStatus(s)} disabled={busy || booking.payment_status === s}
                className={`px-2 py-0.5 uppercase tracking-[0.15em] border ${cls} ${booking.payment_status === s ? "opacity-100 bg-white/5" : "opacity-70 hover:opacity-100"} disabled:cursor-default`}>
                {booking.payment_status === s ? "● " : ""}{l}
              </button>
            ))}
            <span className="ml-auto">Admin actions bypass coupon limits &amp; price locks · every change is logged</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <button data-testid="px-add-card" onClick={() => setShowAddCard(true)} className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-blue-300 border border-blue-500/40 hover:bg-blue-500/10">+ Add Card</button>
          <button data-testid="px-copy-card" onClick={() => setShowCopyCard(true)} className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-blue-300 border border-blue-500/40 hover:bg-blue-500/10">⎘ Copy Card</button>
          <button data-testid="px-outside" onClick={() => setShowOutside(true)} disabled={busy} className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-amber-300 border border-amber-500/40 hover:bg-amber-500/10 disabled:opacity-40">⊕ Payment Outside</button>
          <button data-testid="px-unmark" onClick={unmarkInvoice} disabled={busy || !["paid", "balance_due"].includes(booking.payment_status)} className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] border border-white/20 hover:bg-white/5 disabled:opacity-40">⊘ Unmark Invoice</button>
        </div>

        {/* Card selector + detail */}
        <div className="mb-3">
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Payment Card</label>
          {cards.length === 0 ? (
            <div className="border border-dashed border-white/15 p-3 text-xs text-[#A1A1AA]">No saved cards on file. Customer can add one in their account, or use "+ Add Card".</div>
          ) : (
            <select data-testid="px-card-select" value={selectedCardId} onChange={e => setSelectedCardId(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm">
              {cards.map(c => (
                <option key={c.saved_card_id} value={c.saved_card_id}>
                  •••• {c.last4} · {c.brand} · {c.cardholder_name || "—"} · exp {c.exp_month}/{c.exp_year}
                </option>
              ))}
            </select>
          )}
        </div>
        {selectedCard && (
          <div className="border border-white/10 bg-[#080808] p-3 flex justify-between items-center text-xs">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[#A1A1AA]">Card:</span>
              <span className="text-[#F5F2EB]">{selectedCard.brand} •••• {selectedCard.last4}</span>
              <span className="text-[#A1A1AA]">Expires:</span>
              <span className="text-[#F5F2EB]">{selectedCard.exp_month}/{selectedCard.exp_year}</span>
              <span className="text-[#A1A1AA]">Name:</span>
              <span className="text-blue-300 bg-blue-500/10 px-2 py-0.5">{selectedCard.cardholder_name || "—"}</span>
            </div>
            <button data-testid="px-delete-card" onClick={deleteCard} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-red-400 border border-red-500/40 hover:bg-red-500/10">🗑 Delete Card</button>
          </div>
        )}

        {/* Status banner */}
        {booking.payment_status === "paid" && (
          <div className="mt-3 border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs text-blue-200">
            Reservation marked for invoicing — fully paid ({cents(totalPaidCents)})
          </div>
        )}
        <BalanceDueBanner booking={booking} onChanged={() => { refresh(); onBookingChanged?.(); }}/>

        {/* Summary tiles */}
        <div className="grid grid-cols-5 gap-3 mt-5">
          <MetricBig label="Reservation Total" value={cents(fareCents)} color="text-[#F5F2EB]"/>
          <MetricBig label="Total Paid" value={cents(totalPaidCents)} color="text-emerald-400"/>
          <MetricBig label="Overpayment" value={cents(overpayCents)} color="text-purple-400"/>
          <MetricBig label="Outstanding" value={cents(pendingCents)} color={pendingCents > 0 ? "text-amber-400" : "text-[#71717A]"}/>
          <MetricBig label="Transactions" value={String(payments.length)} color="text-blue-400"/>
        </div>

        {/* Transactions table */}
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] border-b border-white/10">
                <th className="text-left py-2">Amount</th>
                <th className="text-left">Status</th>
                <th className="text-left">Action</th>
                <th className="text-left">Gateway</th>
                <th className="text-left">Card</th>
                <th className="text-left">Transaction ID</th>
                <th className="text-left">Date</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan={8} className="text-[#71717A] py-4 text-center">No transactions yet.</td></tr>
              ) : payments.map((p, i) => (
                <React.Fragment key={i}>
                  <tr className="border-b border-white/5" data-testid={`px-row-${i}`}>
                    {editingLogId === p.log_id ? (
                      <>
                        <td className="py-2">
                          <input
                            data-testid={`px-edit-amount-${i}`}
                            value={editAmount}
                            onChange={(e) => setEditAmount(e.target.value)}
                            type="number" step="0.01"
                            className="w-20 bg-transparent border border-[#D4AF37]/60 px-2 py-1 text-xs text-right text-[#D4AF37] font-mono"
                          />
                        </td>
                        <td><StatusPill status={p.status}/></td>
                        <td>
                          <input
                            data-testid={`px-edit-method-${i}`}
                            value={editMethod}
                            onChange={(e) => setEditMethod(e.target.value)}
                            placeholder="venmo / zelle / cash"
                            className="w-28 bg-transparent border border-[#D4AF37]/60 px-2 py-1 text-xs text-[#F5F2EB]"
                          />
                        </td>
                        <td colSpan={4} className="text-[10px] text-[#71717A]">Editing entry…</td>
                        <td className="text-right">
                          <div className="flex justify-end gap-1">
                            <button data-testid={`px-edit-save-${i}`} onClick={() => saveEdit(p)} className="text-[10px] uppercase tracking-[0.18em] bg-[#D4AF37] text-[#0A0A0A] font-bold px-2 py-1">Save</button>
                            <button data-testid={`px-edit-cancel-${i}`} onClick={cancelEdit} className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] border border-white/15 px-2 py-1">×</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                    <td className={`py-2 font-mono ${(p.amount_cents || 0) < 0 ? "text-red-400" : "text-emerald-400"}`}>{cents(p.amount_cents || 0)}</td>
                    <td><StatusPill status={p.status}/></td>
                    <td className="text-[#A1A1AA]">{p.provider === "square_refund" ? "Refund" : p.provider === "square" || p.provider === "square_saved" ? "Authorize/Capture" : (p.method || "—")}</td>
                    <td className="text-[#A1A1AA]">{p.provider === "square" || p.provider === "square_saved" ? "Square" : (p.provider || "—")}</td>
                    <td className="text-[#A1A1AA]">{p.saved_card_id ? "•••• Card on file" : "—"}</td>
                    <td className="text-[#A1A1AA] font-mono text-[10px]">{p.payment_id ? p.payment_id.slice(0, 18) + "…" : "—"}</td>
                    <td className="text-[#A1A1AA]">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</td>
                    <td className="text-right">
                      <div className="flex justify-end items-center gap-1">
                        {p.status === "paid" && (p.method === "card" || p.method === "card_on_file") && p.payment_id ? (
                          <button data-testid={`px-refund-${i}`} onClick={() => refundPayment(p)} className="text-[10px] uppercase tracking-[0.18em] text-red-400 border border-red-500/40 px-2 py-1 hover:bg-red-500/10">Refund</button>
                        ) : null}
                        {/* Edit pencil — manual / outside / failed only.
                            Square paid charges stay read-only here. */}
                        {!(p.status === "paid" && (p.provider === "square" || p.provider === "square_saved") && p.payment_id) && p.log_id && (
                          <button
                            data-testid={`px-edit-${i}`}
                            onClick={() => beginEdit(p)}
                            title="Edit amount or method"
                            className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] border border-white/15 px-2 py-1 hover:text-[#D4AF37] hover:border-[#D4AF37]/60"
                          >✏️</button>
                        )}
                        {/* Delete shows for manual / outside / failed entries.
                            Lets the dispatcher remove a stray log so they can
                            re-enter the payment as a single clean transaction. */}
                        {!(p.status === "paid" && (p.provider === "square" || p.provider === "square_saved") && p.payment_id) && p.log_id && (
                          <button
                            data-testid={`px-delete-${i}`}
                            onClick={() => deletePaymentLog(p)}
                            title="Remove this entry from the log (does not refund)"
                            className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] border border-white/15 px-2 py-1 hover:text-red-400 hover:border-red-400/60"
                          >🗑</button>
                        )}
                        {p.status === "paid" && p.provider !== "square" && p.provider !== "square_saved" && !(p.method === "card" || p.method === "card_on_file") && (
                          <span className="text-[#71717A] text-[10px]">manual</span>
                        )}
                      </div>
                    </td>
                      </>
                    )}
                  </tr>
                  {p.status === "failed" && (p.error || p.square_status) && (
                    <FailedRowDetail row={p} colSpan={8} idx={i}/>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {showOutside && <OutsidePaymentModal bookingId={bookingId} suggestedAmount={pendingCents / 100} onClose={() => setShowOutside(false)} onDone={() => { setShowOutside(false); refresh(); }}/>}
      {showAddCard && <AddCardHelpModal customerEmail={booking.customer_email} onClose={() => setShowAddCard(false)}/>}
      {showCopyCard && <CopyCardModal booking={booking} onClose={() => setShowCopyCard(false)} onDone={() => { setShowCopyCard(false); refresh(); }}/>}
      {showChargeCard && (
        <ChargeNewCardModal
          booking={booking}
          amount={chargeCents / 100}
          reason={chargeReason || (pendingCents > 0 ? "Remaining balance" : "Dispatcher charge")}
          onClose={() => setShowChargeCard(false)}
          onPaid={() => { setShowChargeCard(false); refresh(); onBookingChanged?.(); }}
        />
      )}
      {showChargeRemaining && (
        <ChargeBalanceModal booking={booking} onClose={() => setShowChargeRemaining(false)}
          onDone={() => { setShowChargeRemaining(false); refresh(); onBookingChanged?.(); }}/>
      )}
    </div>
  );
}

// =============================================================
// FAILED ROW DETAIL — collapses raw Square dumps into a clean reason
// =============================================================
function FailedRowDetail({ row, colSpan, idx }) {
  const [showRaw, setShowRaw] = useState(false);
  const raw = String(row.error || "").replace(/^Payment failed:\s*/i, "");
  // Extract the most useful Square code/detail from either the new clean
  // format ("CODE: detail · CODE2: detail2") OR a raw httpx-style dump
  // ("headers: {...}, body: {'errors': [{'code': 'X', 'detail': 'Y'}], ...}").
  // We try the regex first because old (pre-fix) entries still live in DB.
  const parsed = (() => {
    const out = [];
    // pattern 1: 'code': 'X', 'detail': "Y"  (single OR double quotes, allow whitespace)
    const re = /['"]code['"]\s*:\s*['"]([A-Z_][A-Z0-9_]+)['"][\s,]*['"]detail['"]\s*:\s*['"]([^'"\\]*(?:\\.[^'"\\]*)*)['"]/g;
    let m;
    while ((m = re.exec(raw)) !== null && out.length < 4) {
      const code = m[1];
      const detail = m[2].replace(/\\'/g, "'").replace(/\\"/g, '"');
      // de-dupe
      if (!out.some(o => o.code === code)) out.push({ code, detail });
    }
    return out;
  })();

  const friendlyHint = (code) => ({
    GENERIC_DECLINE:        "Issuer rejected the charge — ask the customer to try a different card or call their bank.",
    CARD_DECLINED:          "Issuer declined — different card recommended.",
    INSUFFICIENT_FUNDS:     "Card has insufficient funds — try a different card.",
    CVV_FAILURE:            "Wrong CVV — re-verify the 3-digit code on the back.",
    INVALID_EXPIRATION:     "Expiration date doesn't match the card — re-verify month/year.",
    TRANSACTION_LIMIT:      "Charge above the card's transaction limit — split into smaller charges or use a different card.",
    CARD_EXPIRED:           "Card is expired — request a new card from the customer.",
    PAYMENT_LIMIT_EXCEEDED: "Hit Square's daily limit for this merchant — contact Square or retry tomorrow.",
    AVS_FAILURE:            "Billing address mismatch — re-verify ZIP / street #.",
    INVALID_CARD:           "Card number invalid — retype it.",
    VERIFY_CVV:             "Issuer wants CVV re-verification.",
  })[code] || "";

  // Truncate any leftover raw text to keep the row compact
  const compact = raw.length > 240 ? raw.slice(0, 240) + "…" : raw;

  return (
    <tr className="border-b border-white/5 bg-red-500/5" data-testid={`px-row-${idx}-error`}>
      <td colSpan={colSpan} className="py-2 px-3 text-[11px] text-red-300">
        <div className="flex items-start gap-2">
          <span className="text-[10px] uppercase tracking-[0.18em] text-red-400 shrink-0 pt-0.5">Why it failed:</span>
          <div className="flex-1 min-w-0">
            {parsed.length > 0 ? (
              <div className="space-y-1">
                {parsed.map((p, i2) => (
                  <div key={i2}>
                    <span className="font-mono text-red-200 font-semibold">{p.code}</span>
                    <span className="text-red-300"> · {p.detail}</span>
                    {friendlyHint(p.code) && (
                      <div className="text-[10px] text-[#A1A1AA] italic mt-0.5">→ {friendlyHint(p.code)}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <span className="font-mono break-all">{compact}</span>
            )}
            <button
              data-testid={`px-row-${idx}-toggle-raw`}
              onClick={() => setShowRaw(v => !v)}
              className="text-[9px] uppercase tracking-[0.18em] text-[#71717A] hover:text-[#A1A1AA] mt-2"
            >{showRaw ? "▾ Hide raw response" : "▸ Show raw response (for support)"}</button>
            {showRaw && (
              <pre className="mt-2 p-2 bg-black/50 text-[9px] text-[#71717A] font-mono overflow-x-auto whitespace-pre-wrap break-all max-h-40 overflow-y-auto">{raw}</pre>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}


// Opens the Square Web Payments SDK card form right inside the Deep View so
// the dispatcher can type the customer's card number, charge it immediately,
// and (optionally) save it to the customer's account for next time.
// =============================================================
function ChargeNewCardModal({ booking, amount, reason, onClose, onPaid }) {
  const charge = amount != null && amount > 0 ? amount : Number(booking.fare || 0);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="charge-card-modal">
      <div className="bg-[#0A0A0A] border border-[#D4AF37]/40 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Charge new card</div>
            <div className="font-serif text-lg text-[#F5F2EB]">Take phone payment</div>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white text-xl px-2">×</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-[11px] text-[#A1A1AA] leading-relaxed">
            Enter the customer&apos;s card details below to charge <span className="text-[#D4AF37]" data-testid="cnc-amount">${charge.toFixed(2)}</span> immediately via Square{reason ? <> · <span className="text-[#F5F2EB]">{reason}</span></> : null}.
            Apple Pay and Google Pay buttons will appear if available.
          </div>
          <PaymentSelector booking={booking} dispatcherMode amountOverride={charge} chargeReason={reason} onPaid={onPaid}/>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const cls = status === "paid" ? "text-emerald-400 border-emerald-400/40"
    : status === "refunded" ? "text-red-400 border-red-500/40"
    : status === "failed" ? "text-red-400 border-red-500/40"
    : "text-[#A1A1AA] border-white/15";
  return <span className={`text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 border ${cls}`}>{status}</span>;
}
function MetricBig({ label, value, color }) {
  return (
    <div className="border border-white/10 bg-[#080808] p-3 text-center">
      <div className={`font-serif text-2xl ${color}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#71717A] mt-1">{label}</div>
    </div>
  );
}

function OutsidePaymentModal({ bookingId, suggestedAmount, onClose, onDone }) {
  const [method, setMethod] = useState("zelle");
  const [amount, setAmount] = useState(suggestedAmount ? suggestedAmount.toFixed(2) : "");
  const [reference, setReference] = useState("");
  const [paidDate, setPaidDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/payments/booking/${bookingId}/outside`, {
        method, amount: Number(amount), reference: reference || null,
        paid_date: paidDate, notes: notes || null,
      });
      toast.success(r.data.fully_paid ? "Marked fully paid" : "Payment recorded");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-white/10 max-w-md w-full p-6" data-testid="outside-payment-modal">
        <div className="flex justify-between items-start mb-4">
          <h3 className="font-serif text-xl">Record outside payment</h3>
          <button onClick={onClose} className="text-[#71717A] text-xl leading-none">×</button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Method</label>
            <select data-testid="op-method" value={method} onChange={e => setMethod(e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 px-2 py-1.5 text-sm">
              <option value="cash">Cash</option><option value="zelle">Zelle</option>
              <option value="venmo">Venmo</option><option value="check">Check</option>
              <option value="paypal">PayPal</option><option value="other">Other</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Amount ($)</label>
              <input data-testid="op-amount" type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"/>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Paid date</label>
              <input data-testid="op-date" type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"/>
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Reference (last 4 / confirmation / memo)</label>
            <input data-testid="op-ref" value={reference} onChange={e => setReference(e.target.value)} className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"/>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Notes</label>
            <textarea data-testid="op-notes" rows={2} value={notes} onChange={e => setNotes(e.target.value)} className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"/>
          </div>
          <div className="flex gap-2 pt-2">
            <button data-testid="op-submit" onClick={submit} disabled={busy} className="btn-gold flex-1 justify-center">{busy ? "Saving…" : "Record payment"}</button>
            <button onClick={onClose} className="btn-ghost">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CopyCardModal({ booking, onClose, onDone }) {
  const [rows, setRows] = useState(null);   // null = loading
  const [pickedId, setPickedId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/copyable", { params: { email: booking.customer_email } })
      .then(r => {
        setRows(r.data || []);
        const first = (r.data || []).find(x => !x.already_copied);
        if (first) setPickedId(first.payment_id);
      })
      .catch(() => setRows([]));
  }, [booking.customer_email]);

  const fmtDt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso; }
  };

  const submit = async () => {
    if (!pickedId) { toast.error("Select a payment first"); return; }
    setBusy(true);
    try {
      const r = await api.post("/payments/cards/from-payment", {
        payment_id: pickedId,
        customer_email: booking.customer_email,
        cardholder_name: booking.customer_name || null,
      });
      toast.success(`Card ${r.data.brand || ""} ····${r.data.last4 || ""} saved to file — ready to charge`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not copy card");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-white/10 max-w-lg w-full p-6" data-testid="copy-card-modal">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-serif text-xl">Copy card from a past reservation</h3>
          <button onClick={onClose} className="text-[#71717A] text-xl leading-none">×</button>
        </div>
        <p className="text-xs text-[#A1A1AA] mb-4">
          Pick one of <span className="text-[#F5F2EB]">{booking.customer_name || booking.customer_email}</span>&apos;s past
          card payments — the card used will be saved to their file (via Square) and become chargeable on this reservation.
          Full card numbers are never exposed.
        </p>

        {rows === null && <div className="text-xs text-[#71717A] py-6 text-center">Loading past payments…</div>}
        {rows !== null && rows.length === 0 && (
          <div className="border border-dashed border-white/15 p-4 text-xs text-[#A1A1AA]" data-testid="copy-card-empty">
            No past Square card payments found for this customer. Use “+ Add Card” or “Charge New Card” instead.
          </div>
        )}
        {rows !== null && rows.length > 0 && (
          <div className="max-h-64 overflow-y-auto space-y-2 mb-4">
            {rows.map(p => (
              <label key={p.payment_id}
                data-testid={`copy-card-row-${p.payment_id}`}
                className={`flex items-center gap-3 border px-3 py-2.5 cursor-pointer transition-colors ${
                  p.already_copied ? "border-white/5 opacity-50 cursor-not-allowed" :
                  pickedId === p.payment_id ? "border-[#D4AF37]/60 bg-[#D4AF37]/5" : "border-white/10 hover:border-white/25"}`}>
                <input type="radio" name="copy-card-pick" className="accent-[#D4AF37]"
                  disabled={p.already_copied}
                  checked={pickedId === p.payment_id}
                  onChange={() => setPickedId(p.payment_id)}/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#F5F2EB]">
                    {p.confirmation_code || p.booking_id} · ${((p.amount_cents || 0) / 100).toFixed(2)}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#71717A]">
                    Paid {fmtDt(p.paid_at)} · {p.vehicle_name || "—"} · {p.method === "card_on_file" ? "Card on file" : "Square"}
                    {p.already_copied && " · already on file"}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button data-testid="copy-card-submit" onClick={submit} disabled={busy || !pickedId}
            className="btn-gold flex-1 justify-center disabled:opacity-50">
            {busy ? "Copying…" : "Save card to file"}
          </button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AddCardHelpModal({ customerEmail, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-white/10 max-w-md w-full p-6" data-testid="add-card-help-modal">
        <div className="flex justify-between items-start mb-4">
          <h3 className="font-serif text-xl">Add a card to file</h3>
          <button onClick={onClose} className="text-[#71717A] text-xl leading-none">×</button>
        </div>
        <p className="text-sm text-[#A1A1AA] mb-3">
          For PCI compliance, cards must be entered by the customer through Square's secure form.
          Send the customer a link to their account page:
        </p>
        <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3 text-xs text-[#D4AF37] font-mono break-all">
          {window.location.origin}/customer
        </div>
        <p className="text-xs text-[#71717A] mt-3">
          Once they sign in with <b>{customerEmail}</b> and add a card, it will appear in this list.
        </p>
        <button onClick={onClose} className="btn-gold w-full justify-center mt-4">Got it</button>
      </div>
    </div>
  );
}

// =============================================================
// INVOICING TAB
// =============================================================
function InvoicingTab({ booking, onSaved }) {
  const code = booking.confirmation_code;
  // For PDF link auth, use whichever contact is available — phone-only
  // bookings have no email and the `?email=` form fails with 400 "Email or
  // phone is required". The backend accepts unified `?contact=` (loose-phone
  // regex matches formatted phone variants).
  const contact = encodeURIComponent(booking.customer_email || booking.customer_phone || "");
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  // A booking is invoice-able as soon as the dispatcher CONFIRMS it (accepted)
  // or any later stage — some clients pay right after confirmation (user
  // request, Feb 2026). Only unconfirmed (pending) and cancelled trips are
  // blocked. Must also not be paid or already on a consolidated invoice.
  const isInvoiceable = ["accepted", "en_route", "on_location", "customer_in_car",
    "completed", "clear_at_destination"].includes(booking.status);
  const alreadyInvoiced = !!booking.consolidated_invoice_id;
  const isPaid = booking.payment_status === "paid";
  const canCreateInvoice = isInvoiceable && !isPaid && !alreadyInvoiced;

  const createInvoice = async () => {
    const hasEmail = !!(booking.customer_email || "").trim();
    const hasPhone = !!(booking.customer_phone || "").trim();
    if (!hasEmail && !hasPhone) {
      toast.error("Add a customer email or phone first — at least one is needed.");
      return;
    }
    setCreating(true);
    try {
      // Step 1 — create the invoice (backend accepts email OR phone)
      const r = await api.post("/invoices/consolidated", {
        customer_email: booking.customer_email || null,
        customer_phone: booking.customer_phone || null,
        customer_name: booking.customer_name || null,
        trip_ids: [booking.booking_id],
      });
      const inv = r.data;
      toast.success(`Invoice ${inv.invoice_number} created`);

      // Step 2 — auto-send via the best available channel(s)
      const sends = [];
      if (hasEmail) {
        sends.push(api.post(`/invoices/consolidated/${inv.invoice_id}/email`)
          .then(() => toast.success(`Emailed to ${booking.customer_email}`))
          .catch(e => toast.error(`Email failed: ${e?.response?.data?.detail || "unknown"}`)));
      }
      if (hasPhone) {
        sends.push(api.post(`/invoices/consolidated/${inv.invoice_id}/sms`)
          .then(() => toast.success(`Texted to ${booking.customer_phone}`))
          .catch(e => toast.error(`SMS failed: ${e?.response?.data?.detail || "unknown"}`)));
      }
      await Promise.allSettled(sends);

      onSaved && onSaved();
      setTimeout(() => navigate("/dispatcher?tab=invoices"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invoice");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="rd-invoicing">
      <Section title="Invoice details">
        <div className="grid grid-cols-2 gap-3">
          <KV k="Invoice #" v={booking.invoice_number || "—"}/>
          <KV k="Confirmation" v={code}/>
          <KV k="Issued" v={booking.created_at ? new Date(booking.created_at).toLocaleDateString() : "—"}/>
          <KV k="Total" v={`$${(booking.fare || 0).toFixed(2)}`} gold/>
        </div>
        {booking.fare_adjusted && (
          <div className="mt-3 text-[11px] text-[#A1A1AA] border-t border-white/10 pt-2" data-testid="rd-fare-adjusted-note">
            <span className="text-[#D4AF37]">Price adjusted</span> from quoted{" "}
            <span className="line-through">${Number(booking.quoted_fare || 0).toFixed(2)}</span>
            {booking.fare_adjusted_by && <> by {booking.fare_adjusted_by}</>}
            {booking.fare_adjusted_reason && <> — “{booking.fare_adjusted_reason}”</>}
          </div>
        )}
      </Section>

      <Section title="Billable invoice">
        {alreadyInvoiced ? (
          <div className="text-sm text-[#A1A1AA]">
            This trip is already part of consolidated invoice
            <span className="text-[#D4AF37] font-mono ml-1">{booking.consolidated_invoice_number || booking.consolidated_invoice_id}</span>.
            <button
              data-testid="open-existing-invoice"
              onClick={() => navigate("/dispatcher?tab=invoices")}
              className="ml-3 text-[#D4AF37] underline hover:no-underline"
            >Open in Invoices tab →</button>
          </div>
        ) : isPaid ? (
          <div className="text-sm text-[#A1A1AA]">This trip is already marked <span className="text-emerald-400">Paid</span> — no invoice needed. Download the receipt below.</div>
        ) : !isInvoiceable ? (
          <div className="text-sm text-[#A1A1AA]" data-testid="invoice-not-ready">
            Invoice can be created once this trip is <span className="text-[#D4AF37]">Confirmed (Accepted)</span> or any later stage. Current status: <span className="text-[#F5F2EB]">{booking.status}</span>.
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-[#A1A1AA]">
              Bill <span className="text-[#F5F2EB]">{booking.customer_name || booking.customer_email || booking.customer_phone}</span> for this trip — generates a payable link (Card · Apple Pay · Zelle · Venmo).
              {!["completed", "clear_at_destination"].includes(booking.status) && (
                <div className="text-[11px] text-amber-300 mt-1" data-testid="invoice-early-note">
                  Trip isn't completed yet ({booking.status}) — invoicing now for clients who pay on confirmation. Extra charges added later (waits, tolls) would need a separate invoice.
                </div>
              )}
              <div className="text-[11px] text-[#71717A] mt-1">
                Auto-sends to{" "}
                {booking.customer_email && booking.customer_phone
                  ? <>email <span className="text-[#D4AF37]">{booking.customer_email}</span> + SMS <span className="text-[#D4AF37]">{booking.customer_phone}</span></>
                  : booking.customer_email
                    ? <>email <span className="text-[#D4AF37]">{booking.customer_email}</span></>
                    : booking.customer_phone
                      ? <>SMS <span className="text-[#D4AF37]">{booking.customer_phone}</span></>
                      : <span className="text-red-400">no email or phone on file — add one in the Overview tab</span>}
              </div>
            </div>
            <button
              data-testid="create-invoice-for-trip"
              disabled={creating || !canCreateInvoice}
              onClick={createInvoice}
              className="btn-gold !py-2 !px-4 text-[11px] whitespace-nowrap"
            >
              <FileText size={12}/> {creating ? "Creating…" : "Create & Send Invoice"}
            </button>
          </div>
        )}
      </Section>

      <Section title="Downloads">
        <div className="flex flex-wrap gap-3">
          <a data-testid="inv-trip-sheet" href={`${API}/bookings/${code}/trip-sheet.pdf?contact=${contact}`} target="_blank" rel="noreferrer"
            className="btn-ghost"><FileText size={14}/> Trip Sheet PDF</a>
          {["paid", "balance_due"].includes(booking.payment_status) && (
            <a data-testid="inv-receipt" href={`${API}/bookings/${code}/receipt.pdf?contact=${contact}`} target="_blank" rel="noreferrer"
              className="btn-gold"><FileText size={14}/> Payment Receipt PDF</a>
          )}
        </div>
      </Section>
    </div>
  );
}

// =============================================================
// SETTLEMENT (inline link to existing tab)
// =============================================================
// Tiny field helper used inline within SettlementInline create form
function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1 block">{label}</label>
      {children}
    </div>
  );
}




// Live flight tracker for the dispatcher Deep View — mirrors the driver-side card but
// adds a one-click "Sync pickup to ETA" button so the dispatcher's calendar stays current.
function FlightTrackerPanel({ booking, onChange }) {
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);

  // Extract these BEFORE the early-return guard so the useEffect hook below stays unconditional.
  const bookingId = booking?.booking_id;
  const flightNumber = booking?.flight_number;
  const pickupIso = booking?.pickup_datetime;

  // Auto-refresh every 5 min when pickup is within ±6h window (-2h grace after pickup
  // in case the flight is still in the air past scheduled arrival).
  useEffect(() => {
    if (!bookingId || !flightNumber || !pickupIso) return undefined;
    const inWindow = () => {
      try {
        const hoursUntil = (new Date(pickupIso).getTime() - Date.now()) / 3600000;
        return hoursUntil <= 6 && hoursUntil >= -2;
      } catch (_e) { return false; }
    };
    const tick = async () => {
      if (!inWindow()) return;
      try {
        await api.post(`/admin/bookings/${bookingId}/refresh-flight`);
        setLastRefreshAt(new Date().toISOString());
        onChange?.();
      } catch (_e) { /* silent — manual button still works */ }
    };
    tick(); // initial check on mount
    const id = setInterval(tick, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(id);
  }, [bookingId, flightNumber, pickupIso, onChange]);

  // Guard against transient undefined booking during the save → refetch cycle.
  // Without this, accessing booking.flight_status crashes the whole page → blank screen.
  if (!booking || !booking.booking_id) return null;
  const fs = booking.flight_status || {};
  const statusLabel = (fs.status || "unknown").replace(/_/g, " ");
  const delay = Number(fs.delay_minutes || 0);
  const arrivalIso = fs.estimated_in || fs.actual_in || fs.scheduled_in;
  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit", timeZoneName: "short" }); }
    catch (_e) { return iso; }
  };

  // FlightAware AeroAPI only has reliable schedules ~2 days out. For bookings
  // further in the future, showing live status returns the WRONG occurrence
  // (the closest available "today" flight instead of the customer's actual
  // future flight). Hide live tracking until 48h before pickup and show only
  // a compact pre-window card with a link to the public FlightAware page +
  // an EDIT button to revise the flight number.
  const hoursUntilPickup = (() => {
    try { return (new Date(booking.pickup_datetime).getTime() - Date.now()) / 3600000; }
    catch (_e) { return Infinity; }
  })();
  const isFarFuture = hoursUntilPickup > 48;

  const cancelledBanner = (booking.flight_cancelled || /cancel/i.test((booking.flight_status || {}).status || "")) ? (
    <div data-testid="flight-cancelled-banner"
      className="border border-red-500/50 bg-red-500/10 p-3 mb-3 text-xs text-red-300">
      <b>✈ FLIGHT CANCELLED</b> — FlightAware reports this flight as cancelled.
      {booking.flight_cancel_notified_at
        ? ` Customer was texted for new flight info at ${new Date(booking.flight_cancel_notified_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}.`
        : " Customer has not been notified yet."}
      {" "}Update the flight number once they reply.
    </div>
  ) : null;

  if (isFarFuture && flightNumber) {
    const compactIdent = String(flightNumber).replace(/\s+/g, "").toUpperCase();
    const trackerUrl = `https://flightaware.com/live/flight/${encodeURIComponent(compactIdent)}`;
    const focusEdit = () => {
      const el = document.querySelector('[data-testid="of-flight"] input, [data-testid="of-flight"]');
      if (el && el.focus) { el.focus(); el.scrollIntoView?.({ behavior: "smooth", block: "center" }); }
    };
    return (
      <div className="mt-3" data-testid="flight-tracker-panel-compact-wrap">
        {cancelledBanner}
        <div className="border border-[#D4AF37]/30 bg-black/30 p-3 flex items-center justify-between" data-testid="flight-tracker-panel-compact">
        <a
          href={trackerUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#3B82F6] hover:underline"
          data-testid="flight-link"
          title="Open live flight tracking in a new tab"
        >
          <Plane size={14}/>
          <span>{compactIdent}</span>
          <ExternalLink size={11}/>
        </a>
        <button
          onClick={focusEdit}
          className="text-[11px] uppercase tracking-[0.18em] text-[#3B82F6] hover:underline"
          data-testid="flight-edit"
          type="button"
        >
          EDIT
        </button>
        </div>
      </div>
    );
  }

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/bookings/${booking.booking_id}/refresh-flight`);
      toast.success(`Flight ${booking.flight_number} updated · status ${r.data?.status || "—"}`);
      setLastRefreshAt(new Date().toISOString());
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setBusy(false);
    }
  };

  const syncPickup = async () => {
    if (!arrivalIso) { toast.error("Refresh flight status first"); return; }
    if (!window.confirm(`Shift pickup to ${fmt(arrivalIso)}? Customer will see the updated pickup time.`)) return;
    setSyncing(true);
    try {
      const r = await api.post(`/admin/bookings/${booking.booking_id}/sync-pickup-to-flight`);
      toast.success(`Pickup updated to ${new Date(r.data.new_pickup_datetime).toLocaleString()}`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const delayColor = delay >= 30 ? "text-red-400" : delay >= 10 ? "text-amber-300" : "text-emerald-300";

  return (
    <div className="border border-[#D4AF37]/30 bg-black/30 p-4 mt-3" data-testid="flight-tracker-panel">
      {cancelledBanner}
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] flex items-center gap-2">
          <Plane size={13}/> Live Flight Tracking · {booking.flight_number}
        </div>
        <div className="flex items-center gap-3">
          {(() => {
            try {
              const hoursUntil = (new Date(booking.pickup_datetime).getTime() - Date.now()) / 3600000;
              if (hoursUntil <= 6 && hoursUntil >= -2) {
                return <span data-testid="ft-auto-on" className="text-[9px] uppercase tracking-[0.2em] text-emerald-300 inline-flex items-center gap-1">● Auto-Refresh</span>;
              }
            } catch (_e) { /* ignore */ }
            return null;
          })()}
          {lastRefreshAt && (
            <span className="text-[9px] uppercase tracking-[0.18em] text-[#71717A]" title={lastRefreshAt}>
              Updated {new Date(lastRefreshAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </span>
          )}
          <button data-testid="ft-refresh" onClick={refresh} disabled={busy} className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-[#D4AF37] disabled:opacity-40 inline-flex items-center gap-1.5">
            <RefreshCcw size={11} className={busy ? "animate-spin" : ""}/> {busy ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>
      {!fs.status ? (
        <div className="text-[11px] text-[#A1A1AA]">No flight status pulled yet. Click <b>Refresh</b> above to query FlightAware.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 text-xs mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mb-0.5">Status</div>
              <div className="text-[#F5F2EB] capitalize">{statusLabel}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mb-0.5">Delay</div>
              <div className={delayColor}>{delay} min</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mb-0.5">Scheduled Arrival</div>
              <div className="text-[#F5F2EB]">{fmt(fs.scheduled_in)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mb-0.5">Estimated Arrival</div>
              <div className="text-[#F5F2EB]">{fmt(arrivalIso)}</div>
            </div>
            {fs.origin && (
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mb-0.5">Route</div>
                <div className="text-[#F5F2EB]">{fs.origin} → {fs.destination || "—"}</div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-3 border-t border-white/10">
            <button data-testid="ft-sync-pickup" onClick={syncPickup} disabled={syncing || !arrivalIso} className="btn-gold !py-2 !px-4 text-[11px] disabled:opacity-40">
              {syncing ? "Updating..." : "Sync Pickup Time to ETA"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}


function SettlementInline({ booking, onChange }) {
  const [settlements, setSettlements] = useState([]);
  const [busy, setBusy] = useState(false);
  // Form state — initialized when the user opens "Create"
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [driverFare, setDriverFare] = useState("");
  const [tip, setTip] = useState("");
  const [custom, setCustom] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [method, setMethod] = useState("zelle");
  const [status, setStatus] = useState("approved");
  const [paidDate, setPaidDate] = useState("");
  const [notes, setNotes] = useState("");

  const refresh = useCallback(() => {
    api.get(`/settlements?driver_id=${booking.assigned_driver_id || ""}`).then(r => {
      setSettlements((r.data || []).filter(s => s.booking_id === booking.booking_id));
    }).catch(() => {});
  }, [booking.assigned_driver_id, booking.booking_id]);
  useEffect(() => { refresh(); }, [refresh]);

  const startCreate = () => {
    // Default driver_fare = 77% of trip fare (matches backend fallback)
    const defaultPay = (Number(booking.fare || 0) * 0.77).toFixed(2);
    setDriverFare(defaultPay);
    setTip("");
    setCustom("");
    setCustomLabel("");
    setMethod("zelle");
    setStatus("approved");
    setPaidDate("");
    setNotes("");
    setEditingId(null);
    setCreating(true);
  };

  const startEdit = (s) => {
    setDriverFare(Number(s.driver_fare || 0).toFixed(2));
    setTip(s.driver_gratuity ? Number(s.driver_gratuity).toFixed(2) : "");
    setCustom(s.custom_charge ? Number(s.custom_charge).toFixed(2) : "");
    setCustomLabel(s.custom_charge_label || "");
    setMethod(s.method || "zelle");
    setStatus(s.status === "paid" ? "paid" : "approved");
    setPaidDate(s.paid_date || "");
    setNotes(s.notes || "");
    setEditingId(s.settlement_id);
    setCreating(false);
  };

  const submitEdit = async () => {
    const df = parseFloat(driverFare);
    if (!Number.isFinite(df) || df <= 0) { toast.error("Enter a valid driver fare"); return; }
    setBusy(true);
    try {
      await api.patch(`/settlements/${editingId}`, {
        driver_fare: df,
        driver_gratuity: parseFloat(tip) || 0,
        custom_charge: parseFloat(custom) || 0,
        custom_charge_label: (customLabel || "").trim() || "",
        method,
        status,
        paid_date: status === "paid" ? (paidDate || null) : null,
        notes: (notes || "").trim(),
      });
      toast.success("Settlement updated");
      setEditingId(null);
      refresh();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update settlement");
    } finally { setBusy(false); }
  };

  const removeSettlement = async (s) => {
    if (!window.confirm(`Delete settlement ${s.receipt_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/settlements/${s.settlement_id}`);
      toast.success("Settlement deleted");
      if (editingId === s.settlement_id) setEditingId(null);
      refresh();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  const submitCreate = async () => {
    const df = parseFloat(driverFare);
    if (!Number.isFinite(df) || df <= 0) {
      toast.error("Enter a valid driver fare");
      return;
    }
    setBusy(true);
    try {
      const body = {
        booking_id: booking.booking_id,
        driver_fare: df,
        driver_gratuity: parseFloat(tip) || 0,
        custom_charge: parseFloat(custom) || 0,
        custom_charge_label: (customLabel || "").trim() || null,
        method,
        status,
        notes: (notes || "").trim() || null,
      };
      const r = await api.post("/settlements", body);
      toast.success(`Settlement created · ${r.data?.receipt_number || "—"}`);
      setCreating(false);
      refresh();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create settlement");
    } finally {
      setBusy(false);
    }
  };

  const payNow = async (s) => {
    if (!window.confirm(`Mark ${s.receipt_number} as paid ($${Number(s.amount || 0).toFixed(2)} via ${s.method})?`)) return;
    try {
      await api.post(`/settlements/${s.settlement_id}/pay`, { method: s.method });
      toast.success("Settlement marked paid");
      refresh();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to mark paid"); }
  };

  const amountPreview = (parseFloat(driverFare) || 0) + (parseFloat(tip) || 0) + (parseFloat(custom) || 0);
  const hasUnpaid = settlements.some(s => s.status !== "paid");
  const canCreate = !!booking.assigned_driver_id && !creating && !editingId && !hasUnpaid;

  return (
    <div className="space-y-4" data-testid="rd-settlement">
      <Section
        title="Driver Settlements"
        actions={
          <div className="flex items-center gap-2">
            <button data-testid="rd-settle-refresh" onClick={refresh} className="btn-ghost !py-1.5 !px-3 text-[10px]"><RefreshCcw size={11}/>Refresh</button>
            <button data-testid="rd-settle-create-btn" onClick={startCreate} disabled={!canCreate}
              className="btn-gold !py-1.5 !px-3 text-[10px] disabled:opacity-30 disabled:cursor-not-allowed"
              title={!booking.assigned_driver_id ? "Assign a driver first" : hasUnpaid ? "Pay or delete the open settlement first" : ""}>+ Create Settlement</button>
          </div>
        }>
        <div className="text-[#71717A] text-[11px] -mt-2 mb-4">Manage driver settlements for this reservation</div>
        {!booking.assigned_driver_id && <div className="text-[#A1A1AA] text-xs">Assign a driver to enable settlement.</div>}

        {booking.assigned_driver_id && settlements.length === 0 && !creating && (
          <div className="text-[#A1A1AA] text-xs mb-3" data-testid="rd-settle-empty">No settlement created yet for driver {booking.assigned_driver_name || "—"}.</div>
        )}

        {(creating || editingId) && (
          <div className="border border-[#D4AF37]/30 bg-black/30 p-4 space-y-3" data-testid={editingId ? "rd-settle-edit-form" : "rd-settle-create-form"}>
            <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.2em]">{editingId ? "Edit settlement" : "New settlement"} · {booking.assigned_driver_name}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">Trip fare ${Number(booking.fare || 0).toFixed(2)} · Default 77% driver pay</div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Driver Fare ($)"><input data-testid="rd-settle-fare" type="number" step="0.01" value={driverFare} onChange={e => setDriverFare(e.target.value)} className="field-input"/></Field>
              <Field label="Driver Tip ($)"><input data-testid="rd-settle-tip" type="number" step="0.01" value={tip} onChange={e => setTip(e.target.value)} placeholder="0.00" className="field-input"/></Field>
              <Field label="Extra Charge ($)"><input data-testid="rd-settle-custom" type="number" step="0.01" value={custom} onChange={e => setCustom(e.target.value)} placeholder="0.00" className="field-input"/></Field>
              <Field label="Extra Label"><input data-testid="rd-settle-custom-label" type="text" value={customLabel} onChange={e => setCustomLabel(e.target.value)} placeholder="e.g. Toll, Wait" className="field-input"/></Field>
              <Field label="Method"><select data-testid="rd-settle-method" value={method} onChange={e => setMethod(e.target.value)} className="field-input">
                <option value="zelle">Zelle</option><option value="venmo">Venmo</option><option value="cashapp">Cash App</option><option value="cash">Cash</option><option value="check">Check</option><option value="other">Other</option>
              </select></Field>
              <Field label="Status"><select data-testid="rd-settle-status" value={status} onChange={e => setStatus(e.target.value)} className="field-input">
                <option value="approved">Approved (not yet paid)</option><option value="paid">Paid</option>
              </select></Field>
              {editingId && status === "paid" && (
                <Field label="Paid On"><input data-testid="rd-settle-paid-date" type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} className="field-input"/></Field>
              )}
            </div>
            <Field label="Notes (optional)"><textarea data-testid="rd-settle-notes" rows={2} value={notes} onChange={e => setNotes(e.target.value)} className="field-input resize-none"/></Field>
            <div className="flex items-baseline justify-between border-t border-white/10 pt-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Total settlement</span>
              <span className="font-serif text-2xl text-[#D4AF37]" data-testid="rd-settle-total">${amountPreview.toFixed(2)}</span>
            </div>
            <div className="flex gap-2 justify-end">
              <button data-testid="rd-settle-cancel" onClick={() => { setCreating(false); setEditingId(null); }} disabled={busy} className="text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-white px-3 py-2 disabled:opacity-40">Cancel</button>
              <button data-testid="rd-settle-save" onClick={editingId ? submitEdit : submitCreate} disabled={busy} className="btn-gold !py-2 !px-5 text-[11px] disabled:opacity-40">
                {busy ? "Saving..." : editingId ? "Save Changes" : "Create Settlement"}
              </button>
            </div>
          </div>
        )}

        {settlements.length > 0 && (
          <div className="border border-white/10 overflow-x-auto" data-testid="rd-settlements-table">
            <table className="w-full text-xs">
              <thead className="bg-black/40 text-[9px] uppercase tracking-[0.18em] text-[#71717A]">
                <tr>
                  <th className="text-left px-4 py-2.5">Driver</th>
                  <th className="text-left px-3 py-2.5">Amount</th>
                  <th className="text-left px-3 py-2.5">Custom charges</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="text-left px-3 py-2.5">Date</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {settlements.map(s => {
                  const isPaid = s.status === "paid";
                  const date = s.paid_date || s.approved_date || (s.created_at || "").slice(0, 10);
                  return (
                    <tr key={s.settlement_id} data-testid={`rd-settlement-${s.settlement_id}`}
                      className={`border-t ${editingId === s.settlement_id ? "border-[#D4AF37]/50 bg-[#D4AF37]/5" : "border-white/5"}`}>
                      <td className="px-4 py-3 align-top">
                        <div className="text-[#F5F2EB]">{s.driver_name}</div>
                        <div className="text-[#71717A] text-[10px] mt-0.5 font-mono">{s.receipt_number}</div>
                      </td>
                      <td className="px-3 py-3 align-top whitespace-nowrap">
                        <div className="text-[#F5F2EB] font-mono">${Number(s.amount || 0).toFixed(2)}</div>
                        <div className="text-[#71717A] text-[10px] mt-0.5">
                          Fare: ${Number(s.driver_fare || 0).toFixed(2)}
                          {Number(s.driver_gratuity) > 0 && ` · Tip: $${Number(s.driver_gratuity).toFixed(2)}`}
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top">
                        {Number(s.custom_charge) > 0 ? (
                          <span className="settle-chip inline-block text-[10px] px-2 py-0.5 border border-sky-400/40 text-sky-300 rounded-full whitespace-nowrap">
                            {s.custom_charge_label || "Extra"} ${Number(s.custom_charge).toFixed(2)}
                          </span>
                        ) : <span className="text-[#71717A]">—</span>}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <span className={`inline-block text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 border ${isPaid ? "text-emerald-300 border-emerald-400/40" : "text-[#D4AF37] border-[#D4AF37]/40"}`}>
                          {s.status}
                        </span>
                        <div className="text-[#71717A] text-[10px] mt-1 uppercase tracking-[0.12em]">
                          via {s.method}{s.edit_history?.length > 0 && <span title={`Last edited ${s.edit_history[s.edit_history.length - 1].at}`}> · edited ×{s.edit_history.length}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top text-[#A1A1AA] whitespace-nowrap">{date || "—"}</td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center justify-end gap-3 text-[10px] uppercase tracking-[0.15em] whitespace-nowrap">
                          <a data-testid={`rd-settle-view-${s.settlement_id}`} href={`${API}/settlements/${s.settlement_id}/receipt.pdf`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[#A1A1AA] hover:text-white"><Eye size={12}/>View</a>
                          <button data-testid={`rd-settle-edit-${s.settlement_id}`} onClick={() => startEdit(s)}
                            className="inline-flex items-center gap-1 text-[#D4AF37] hover:underline"><Pencil size={12}/>Edit</button>
                          {!isPaid && (
                            <button data-testid={`rd-settle-pay-${s.settlement_id}`} onClick={() => payNow(s)}
                              className="inline-flex items-center gap-1 text-emerald-300 hover:underline"><DollarSign size={12}/>Pay</button>
                          )}
                          <button data-testid={`rd-settle-delete-${s.settlement_id}`} onClick={() => removeSettlement(s)}
                            className="inline-flex items-center gap-1 text-red-400 hover:underline"><Trash2 size={12}/>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

// =============================================================
// HISTORY / TIMELINE
// =============================================================
function HistoryTab({ bookingId }) {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get(`/bookings/${bookingId}/timeline-events`).then(r => setEvents(r.data || [])).catch(() => {}); }, [bookingId]);
  return (
    <Section title="Audit trail" testid="rd-history">
      {events.length === 0 ? <div className="text-[#A1A1AA] text-xs">No events recorded.</div> :
        <div className="space-y-1">
          {events.slice().reverse().map((e, i) => (
            <div key={i} className="border-l-2 border-[#D4AF37]/30 pl-3 py-1.5 text-xs">
              <div className="text-[#F5F2EB]">{e.event}{e.by ? ` · by ${e.by}` : ""}</div>
              <div className="text-[#71717A] text-[10px] uppercase tracking-[0.18em] mt-0.5">{e.at}</div>
              {e.changes && <ul className="text-[#A1A1AA] text-[11px] mt-1 ml-2">{e.changes.map((c, j) => <li key={j}>• {c}</li>)}</ul>}
            </div>
          ))}
        </div>
      }
    </Section>
  );
}
function TimelineTab({ bookingId }) {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get(`/bookings/${bookingId}/timeline-events`).then(r => setEvents((r.data || []).filter(e => (e.event || "").startsWith("status:") || (e.event || "").startsWith("geofence") || (e.event || "").startsWith("payment")))).catch(() => {}); }, [bookingId]);
  return (
    <Section title="Trip timeline" testid="rd-timeline">
      {events.length === 0 ? <div className="text-[#A1A1AA] text-xs">No status or payment events yet.</div> :
        <div className="space-y-2">
          {events.map((e, i) => (
            <div key={i} className="flex gap-3 items-start">
              <div className="w-2 h-2 rounded-full bg-[#D4AF37] mt-1.5 flex-shrink-0"/>
              <div className="text-xs">
                <div className="text-[#F5F2EB]">{e.event}</div>
                <div className="text-[#71717A] text-[10px] uppercase tracking-[0.18em] mt-0.5">{e.at}</div>
              </div>
            </div>
          ))}
        </div>
      }
    </Section>
  );
}

// =============================================================
// REMINDERS
// =============================================================
function RemindersTab({ bookingId, booking, onChange }) {
  const [list, setList] = useState([]);
  const [addMode, setAddMode] = useState(null);       // null | "quick" | "custom"
  const [quickTarget, setQuickTarget] = useState("customer");
  const [custom, setCustom] = useState({ target: "customer", fire_at: "", message: "" });
  const [editingId, setEditingId] = useState(null);   // reminder being time-edited
  const [editVal, setEditVal] = useState("");
  const [busy, setBusy] = useState(false);
  const muted = Boolean(booking?.reminders_muted);

  const refresh = useCallback(() => {
    api.get(`/bookings/${bookingId}/reminders`).then(r => setList(r.data || [])).catch(() => {});
  }, [bookingId]);
  useEffect(() => { refresh(); }, [refresh]);

  const reschedule = async () => {
    try {
      const r = await api.post(`/bookings/${bookingId}/reminders/reschedule`);
      toast.success(`Rescheduled · ${r.data.created} pending`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const sendNow = async (rid) => {
    try { await api.post(`/bookings/${bookingId}/reminders/${rid}/send-now`); toast.success("Sent"); refresh(); }
    catch (e) { toast.error("Send failed"); }
  };
  const cancel = async (rid) => {
    try { await api.delete(`/bookings/${bookingId}/reminders/${rid}`); toast.success("Reminder turned off"); refresh(); }
    catch (e) { toast.error("Cancel failed"); }
  };
  const reenable = async (rid) => {
    try { await api.patch(`/bookings/${bookingId}/reminders/${rid}`, { reenable: true }); toast.success("Reminder re-enabled"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not re-enable"); }
  };
  const saveEdit = async (rid) => {
    if (!editVal) return setEditingId(null);
    try {
      await api.patch(`/bookings/${bookingId}/reminders/${rid}`, { fire_at: editVal });
      toast.success("Reminder time updated");
      setEditingId(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update time"); }
  };
  const toggleMute = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${bookingId}/reminders/mute`, { muted: !muted });
      toast.success(!muted
        ? `Customer reminders muted${r.data.cancelled ? ` · ${r.data.cancelled} pending turned off` : ""} — driver reminders stay on`
        : `Customer reminders back on${r.data.restored ? ` · ${r.data.restored} restored` : ""}`);
      onChange && onChange();
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const addQuick = async (hours) => {
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/reminders`, { target: quickTarget, hours_before: hours });
      toast.success(`${quickTarget === "driver" ? "Driver" : "Customer"} reminder added — ${hours >= 1 ? `${hours}h` : `${Math.round(hours * 60)}m`} before pickup`);
      setAddMode(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add reminder"); }
    finally { setBusy(false); }
  };
  const addCustom = async () => {
    if (!custom.fire_at) return toast.error("Pick a date & time");
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/reminders`, {
        target: custom.target, fire_at: custom.fire_at,
        message: custom.message.trim() || undefined,
      });
      toast.success("Custom reminder scheduled");
      setAddMode(null);
      setCustom({ target: "customer", fire_at: "", message: "" });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add reminder"); }
    finally { setBusy(false); }
  };

  const pending = list.filter(r => r.status === "pending");
  const sent = list.filter(r => r.status === "sent");
  const cancelled = list.filter(r => r.status !== "pending" && r.status !== "sent");
  const [showCancelled, setShowCancelled] = useState(false);
  const fmtWhen = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
  };
  const remLabel = (r) => {
    const h = Number(r.hours_before || 0);
    const when = h >= 1 ? `${h}h before` : (h > 0 ? `${Math.round(h * 60)}m before` : "custom time");
    return `${r.target === "driver" ? "DRIVER" : r.target === "customer_payment" ? "PAYMENT NUDGE" : "CUSTOMER"} · ${r.target === "customer_payment" ? `${r.hours_after}h after` : when}`;
  };
  const canReenable = (r) => r.status === "cancelled" && r.fire_at && new Date(r.fire_at) > new Date();

  return (
    <div className="space-y-4" data-testid="rd-reminders">
      {/* ── Per-trip customer mute ── */}
      <div className={`border p-4 flex items-center justify-between gap-3 ${muted ? "border-amber-500/40 bg-amber-500/10" : "border-white/10 bg-black/30"}`}
           data-testid="rem-mute-panel">
        <div>
          <div className="text-sm text-[#F5F2EB]">{muted ? "Customer reminders are OFF for this trip" : "Customer reminders are ON"}</div>
          <div className="text-[11px] text-[#71717A] mt-0.5">
            {muted ? "Client won't get any reminder texts (meeting mode). Driver reminders still fire." : "Turn off for clients in meetings who don't want reminder texts. Driver reminders stay on."}
          </div>
        </div>
        <button data-testid="rem-mute-toggle" onClick={toggleMute} disabled={busy}
                className={`shrink-0 !py-2 !px-4 text-[10px] uppercase tracking-[0.18em] border transition-colors disabled:opacity-50 ${muted ? "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10" : "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"}`}>
          {muted ? "Turn back on" : "Mute customer"}
        </button>
      </div>

      <Section title="Reminder schedule" actions={
        <div className="flex gap-2">
          <button data-testid="rem-reschedule" onClick={reschedule} className="btn-ghost !py-1.5 !px-3 text-[10px]"><RefreshCcw size={12}/> Reschedule</button>
          <button data-testid="rem-add-quick" onClick={() => setAddMode(m => m === "quick" ? null : "quick")} className="btn-gold !py-1.5 !px-3 text-[10px]">+ Quick reminder</button>
          <button data-testid="rem-add-custom" onClick={() => setAddMode(m => m === "custom" ? null : "custom")} className="btn-ghost !py-1.5 !px-3 text-[10px]">+ Custom reminder</button>
        </div>
      }>
        <div className="text-[11px] text-[#A1A1AA] mb-3">
          Auto-schedule — <b className="text-[#D4AF37]">Customer:</b> 2h before pickup &nbsp;|&nbsp; <b className="text-[#D4AF37]">Driver:</b> 2h · 1h before pickup
        </div>

        {addMode === "quick" && (
          <div className="border border-[#D4AF37]/30 bg-black/30 p-3 mb-3" data-testid="rem-quick-form">
            <div className="flex items-center gap-3 flex-wrap">
              <select data-testid="rem-quick-target" value={quickTarget} onChange={e => setQuickTarget(e.target.value)}
                      className="bg-transparent border border-white/10 px-2 py-1.5 text-xs">
                <option value="customer">Customer</option>
                <option value="driver">Driver</option>
              </select>
              {[24, 12, 4, 2, 1, 0.5].map(h => (
                <button key={h} data-testid={`rem-quick-${h}`} onClick={() => addQuick(h)} disabled={busy}
                        className="border border-white/15 px-3 py-1.5 text-xs text-[#F5F2EB] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-50">
                  {h >= 1 ? `${h}h` : `${Math.round(h * 60)}m`} before
                </button>
              ))}
            </div>
          </div>
        )}

        {addMode === "custom" && (
          <div className="border border-[#D4AF37]/30 bg-black/30 p-3 mb-3 space-y-2" data-testid="rem-custom-form">
            <div className="flex items-center gap-3 flex-wrap">
              <select data-testid="rem-custom-target" value={custom.target} onChange={e => setCustom(c => ({ ...c, target: e.target.value }))}
                      className="bg-transparent border border-white/10 px-2 py-1.5 text-xs">
                <option value="customer">Customer</option>
                <option value="driver">Driver</option>
              </select>
              <input data-testid="rem-custom-datetime" type="datetime-local" value={custom.fire_at}
                     onChange={e => setCustom(c => ({ ...c, fire_at: e.target.value }))}
                     className="bg-transparent border border-white/10 px-2 py-1.5 text-xs [color-scheme:dark]"/>
              <button data-testid="rem-custom-save" onClick={addCustom} disabled={busy} className="btn-gold !py-1.5 !px-3 text-[10px] disabled:opacity-50">Schedule</button>
            </div>
            <input data-testid="rem-custom-message" value={custom.message}
                   onChange={e => setCustom(c => ({ ...c, message: e.target.value }))} maxLength={320}
                   placeholder="Optional custom text — leave blank for the standard reminder message"
                   className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-xs"/>
          </div>
        )}

        {pending.length === 0 ? <div className="text-[#71717A] text-xs">No pending reminders.</div> :
          <div className="space-y-2">
            {pending.map(r => (
              <div key={r.reminder_id} className="border border-white/10 p-3 flex justify-between items-center gap-2 flex-wrap text-xs" data-testid={`rem-row-${r.reminder_id}`}>
                <div className="min-w-0">
                  <div className="text-[#F5F2EB]">
                    {remLabel(r)}
                    {r.custom && <span className="ml-2 text-[9px] uppercase tracking-[0.15em] text-[#D4AF37] border border-[#D4AF37]/30 px-1.5 py-0.5">custom</span>}
                  </div>
                  <div className="text-[#71717A] text-[10px] uppercase tracking-[0.18em] mt-0.5">Fires {new Date(r.fire_at).toLocaleString()} · {r.channel}</div>
                  {r.custom_message && <div className="text-[10px] text-[#A1A1AA] mt-1 italic">"{r.custom_message}"</div>}
                </div>
                {editingId === r.reminder_id ? (
                  <div className="flex items-center gap-2">
                    <input data-testid={`rem-edit-input-${r.reminder_id}`} type="datetime-local" value={editVal} onChange={e => setEditVal(e.target.value)}
                           className="bg-transparent border border-[#D4AF37]/40 px-2 py-1 text-xs [color-scheme:dark]"/>
                    <button data-testid={`rem-edit-save-${r.reminder_id}`} onClick={() => saveEdit(r.reminder_id)} className="text-[#D4AF37] text-[10px] uppercase tracking-[0.18em] hover:underline">Save</button>
                    <button onClick={() => setEditingId(null)} className="text-[#71717A] text-[10px] uppercase tracking-[0.18em] hover:underline">×</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button data-testid={`rem-edit-${r.reminder_id}`} onClick={() => { setEditingId(r.reminder_id); setEditVal(""); }} className="text-[#A1A1AA] text-[10px] uppercase tracking-[0.18em] hover:text-[#D4AF37] hover:underline">Edit time</button>
                    <button data-testid={`rem-send-${r.reminder_id}`} onClick={() => sendNow(r.reminder_id)} className="text-[#D4AF37] text-[10px] uppercase tracking-[0.18em] hover:underline">Send now</button>
                    <button data-testid={`rem-cancel-${r.reminder_id}`} onClick={() => cancel(r.reminder_id)} className="text-red-400 text-[10px] uppercase tracking-[0.18em] hover:underline">Turn off</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        }
      </Section>
      {(sent.length > 0 || cancelled.length > 0) && (
        <Section title="History" actions={
          cancelled.length > 0 && (
            <button data-testid="rem-toggle-cancelled" onClick={() => setShowCancelled(v => !v)}
              className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] hover:text-[#F5F2EB]">
              {showCancelled ? "Hide" : "Show"} cancelled ({cancelled.length})
            </button>
          )
        }>
          {sent.length === 0 && !showCancelled && (
            <div className="text-[#71717A] text-xs" data-testid="rem-history-empty">No reminders sent yet.</div>
          )}
          <div className="space-y-1.5">
            {sent.map(r => (
              <div key={r.reminder_id} className="border-l-2 border-emerald-400/40 pl-3 text-xs py-1">
                <div className="text-[#F5F2EB]">{r.target} · {r.hours_before}h · <span className="text-emerald-400">sent</span></div>
                <div className="text-[#71717A] text-[10px] uppercase tracking-[0.18em]">{fmtWhen(r.sent_at || r.fire_at)}</div>
              </div>
            ))}
            {showCancelled && cancelled.map(r => (
              <div key={r.reminder_id} className="border-l-2 border-white/10 pl-3 text-xs py-1 opacity-70 flex justify-between items-center gap-2">
                <div>
                  <div className="text-[#F5F2EB]">{r.target} · {r.hours_before}h · <span className="text-[#71717A]">{r.status}{r.cancelled_reason ? ` (${r.cancelled_reason})` : ""}</span></div>
                  <div className="text-[#71717A] text-[10px] uppercase tracking-[0.18em]">{fmtWhen(r.cancelled_at || r.fire_at)}</div>
                </div>
                {canReenable(r) && (
                  <button data-testid={`rem-reenable-${r.reminder_id}`} onClick={() => reenable(r.reminder_id)}
                          className="text-emerald-400 text-[10px] uppercase tracking-[0.18em] hover:underline shrink-0">Re-enable</button>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

// =============================================================
// SHARED UI
// =============================================================
function Section({ title, children, actions, testid }) {
  return (
    <section className="border border-white/10 bg-[#0A0A0A] p-5" data-testid={testid}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-serif text-base text-[#D4AF37]">{title}</h3>
        {actions}
      </div>
      {children}
    </section>
  );
}
function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{label}</label>
      <input type={type} data-testid={testid} value={value || ""} onChange={e => onChange(e.target.value)}
        className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB] focus:border-[#D4AF37]/60 focus:outline-none"/>
    </div>
  );
}
function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{label}</label>
      <select data-testid={testid} value={value || ""} onChange={e => onChange(e.target.value)}
        className="w-full bg-[#0a0a0a] border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB] focus:border-[#D4AF37]/60 focus:outline-none">
        {options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </div>
  );
}
function ReadOnlyField({ label, value }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{label}</label>
      <div className="w-full bg-white/5 border border-white/10 px-2 py-1.5 text-sm text-[#A1A1AA]">{value}</div>
    </div>
  );
}
function Checkbox({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" data-testid={testid} checked={!!checked} onChange={e => onChange(e.target.checked)} className="accent-[#D4AF37]"/>
      <span className="text-xs text-[#F5F2EB]">{label}</span>
    </label>
  );
}
function KV({ k, v, gold }) {
  return (
    <div className="border border-white/10 bg-[#080808] p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{k}</div>
      <div className={`font-serif text-base ${gold ? "text-[#D4AF37]" : "text-[#F5F2EB]"}`}>{v}</div>
    </div>
  );
}


// =============================================================
// CUSTOMER VIEW PANEL — preview of /track + recent emails/SMS
// =============================================================
function CustomerViewPanel({ bookingId, onClose }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("track"); // track | email | sms

  useEffect(() => {
    api.get(`/bookings/${bookingId}/customer-view`).then(r => setData(r.data)).catch(() => setData({ error: true }));
  }, [bookingId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="customer-view-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}/>
      <div onClick={e => e.stopPropagation()}
        className="relative w-full max-w-3xl max-h-[90vh] bg-[#0a0a0a] border border-white/10 rounded-lg overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        data-testid="customer-view-panel">
        <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 p-4 flex justify-between items-center z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Customer view</div>
            <div className="font-serif text-lg">What the customer sees right now</div>
          </div>
          <button data-testid="cv-close" onClick={onClose} className="text-[#71717A] text-2xl leading-none">×</button>
        </div>

        {!data ? <div className="p-6 text-[#A1A1AA]">Loading…</div> :
         data.error ? <div className="p-6 text-red-400">Could not load customer view.</div> : (
          <div className="p-4 space-y-4">
            <div className="flex gap-1 border-b border-white/10">
              {[["track", "Live track"], ["email", `Emails (${data.email_log.length})`], ["sms", `SMS (${data.sms_log.length})`]].map(([k, l]) => (
                <button key={k} data-testid={`cv-tab-${k}`} onClick={() => setView(k)}
                  className={`px-3 py-2 text-xs uppercase tracking-[0.18em] ${view === k ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-[#A1A1AA]"}`}>{l}</button>
              ))}
            </div>

            {view === "track" && (
              <div className="space-y-3">
                <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Tracking link</div>
                  <a href={data.track_url_with_email} target="_blank" rel="noreferrer" className="text-xs text-blue-300 break-all underline">
                    {data.track_url_with_email}
                  </a>
                </div>
                <KV k="Status" v={(data.track.booking.status || "").toUpperCase()} gold={data.track.booking.status === "completed"}/>
                <KV k="Vehicle" v={data.track.booking.vehicle_name}/>
                <KV k="Pickup" v={data.track.booking.pickup_address}/>
                {data.track.booking.dropoff_address && <KV k="Drop-off" v={data.track.booking.dropoff_address}/>}
                <KV k="Driver visible to customer" v={data.track.booking.assigned_driver_name || "Not yet assigned"}/>
                <KV k="Fare" v={`$${(data.track.booking.fare || 0).toFixed(2)}`} gold/>
                <KV k="Payment" v={(data.track.booking.payment_status || "—").toUpperCase()}/>
                {data.track.driver_location && (
                  <div className="border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs">
                    <div className="text-emerald-400 mb-1">Driver location is live</div>
                    <div className="text-[#A1A1AA]">Last update: {data.track.driver_location.updated_at}</div>
                  </div>
                )}
              </div>
            )}

            {view === "email" && (
              <div className="space-y-2">
                {data.email_log.length === 0 ? <div className="text-[#A1A1AA] text-sm">No emails sent yet.</div> :
                  data.email_log.map((e, i) => (
                    <div key={i} className="border border-white/10 bg-[#080808] p-3 text-xs">
                      <div className="flex justify-between mb-1">
                        <span className="text-[#F5F2EB]">{e.subject || "(no subject)"}</span>
                        <span className={e.status === "sent" ? "text-emerald-400" : e.status === "MOCKED" ? "text-amber-400" : "text-[#A1A1AA]"}>{e.status || "—"}</span>
                      </div>
                      <div className="text-[#71717A] text-[10px] uppercase tracking-[0.18em]">{e.tag} · {e.sent_at}</div>
                      <div className="text-[#A1A1AA] mt-1">To: {e.to}</div>
                      {e.body_preview && <div className="mt-2 text-[#A1A1AA] italic">{e.body_preview}</div>}
                    </div>
                  ))}
              </div>
            )}

            {view === "sms" && (
              <div className="space-y-2">
                {data.sms_log.length === 0 ? <div className="text-[#A1A1AA] text-sm">No SMS sent yet.</div> :
                  data.sms_log.map((s, i) => (
                    <div key={i} className="border border-white/10 bg-[#080808] p-3 text-xs">
                      <div className="flex justify-between mb-1">
                        <span className="text-[#A1A1AA]">To: {s.to}</span>
                        <span className={s.status === "sent" ? "text-emerald-400" : s.status === "MOCKED" ? "text-amber-400" : "text-[#A1A1AA]"}>{s.status || "—"}</span>
                      </div>
                      <div className="text-[#71717A] text-[10px] uppercase tracking-[0.18em] mb-1">{s.tag} · {s.sent_at}</div>
                      <div className="text-[#F5F2EB] whitespace-pre-wrap">{s.body}</div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}



// =============================================================
// COMMUNICATION MODAL  — Client / Driver tabs with email + SMS dispatch
// =============================================================
const CLIENT_COMMS = [
  { kind: "client_driver_accepted", title: "Driver Accepted", description: "Send driver-accepted notification to the customer", icon: "🎉", channels: ["email", "sms"] },
  { kind: "client_confirmation",    title: "Confirmation",    description: "Send the booking confirmation to the customer",     icon: "✉️", channels: ["email", "sms"] },
  { kind: "client_payment_receipt", title: "Payment Receipt", description: "Send the comprehensive payment receipt to the customer", icon: "💳", channels: ["email", "sms"] },
];

const DRIVER_COMMS = [
  { kind: "driver_accepted",   title: "Driver Accepted",   description: "Re-send the trip-accepted notification to the driver", icon: "🎉", channels: ["email", "sms"] },
  { kind: "driver_assignment", title: "Driver Assignment", description: "Send the trip-assignment notification to the driver", icon: "🚗", channels: ["email", "sms"] },
];

function CommunicationModal({ booking, onClose }) {
  const [tab, setTab] = useState("client");
  const [pending, setPending] = useState(null);

  const send = async (kind, channel) => {
    const key = `${kind}:${channel}`;
    setPending(key);
    try {
      await api.post(`/bookings/${booking.booking_id}/send-communication`, { kind, channel });
      toast.success(`Sent via ${channel.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setPending(null);
    }
  };

  const items = tab === "client" ? CLIENT_COMMS : DRIVER_COMMS;
  const audienceLabel = tab === "client" ? "customer" : "driver";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="communication-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-white text-[#050505] border border-black/10 rounded-lg overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200" data-testid="communication-panel">
        <header className="sticky top-0 z-10 bg-white border-b border-black/10 px-6 py-5 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <Send size={20} className="text-[#004EEB] mt-1 shrink-0"/>
            <div>
              <div className="text-2xl font-bold tracking-tight">Send Communication</div>
              <div className="text-sm text-[#71717A] mt-1">Select a communication option to send immediately.</div>
            </div>
          </div>
          <button data-testid="communication-close" onClick={onClose} className="text-[#71717A] hover:text-[#050505] -mt-1">
            <X size={20}/>
          </button>
        </header>

        <div className="px-6 pt-5">
          <div className="grid grid-cols-2 gap-0 border border-black/10 bg-[#F4F4F5] p-1 rounded">
            <button
              data-testid="comm-tab-client"
              onClick={() => setTab("client")}
              className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-all rounded ${tab === "client" ? "bg-white text-[#050505] shadow-sm" : "text-[#71717A] hover:text-[#050505]"}`}
            >
              <User size={15}/> Client
            </button>
            <button
              data-testid="comm-tab-driver"
              onClick={() => setTab("driver")}
              className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-all rounded ${tab === "driver" ? "bg-white text-[#050505] shadow-sm" : "text-[#71717A] hover:text-[#050505]"}`}
            >
              <Car size={15}/> Driver
            </button>
          </div>
        </div>

        <div className="p-6 space-y-3" data-testid={`comm-list-${tab}`}>
          {items.map(item => (
            <div key={item.kind}
              data-testid={`comm-row-${item.kind}`}
              className="border border-black/10 rounded p-4 flex items-start justify-between gap-4 hover:border-[#004EEB] transition">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 bg-[#EFF6FF] rounded flex items-center justify-center text-lg shrink-0">{item.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-base">{item.title}</div>
                  <div className="text-sm text-[#52525B] mt-0.5">{item.description.replace("the customer", audienceLabel).replace("the driver", audienceLabel)}</div>
                  <div className="mt-2 inline-flex px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] border border-black/15 rounded text-[#71717A]">{audienceLabel}</div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                {item.channels.includes("email") && (
                  <button
                    data-testid={`comm-send-${item.kind}-email`}
                    disabled={pending === `${item.kind}:email`}
                    onClick={() => send(item.kind, "email")}
                    className="inline-flex items-center gap-1.5 border-2 border-[#004EEB] text-[#004EEB] hover:bg-[#004EEB] hover:text-white transition px-3 py-2 text-xs font-semibold uppercase tracking-[0.15em] rounded disabled:opacity-50"
                  >
                    <Mail size={13}/> {pending === `${item.kind}:email` ? "..." : "Email"}
                  </button>
                )}
                {item.channels.includes("sms") && (
                  <button
                    data-testid={`comm-send-${item.kind}-sms`}
                    disabled={pending === `${item.kind}:sms`}
                    onClick={() => send(item.kind, "sms")}
                    className="inline-flex items-center gap-1.5 border-2 border-[#004EEB] text-[#004EEB] hover:bg-[#004EEB] hover:text-white transition px-3 py-2 text-xs font-semibold uppercase tracking-[0.15em] rounded disabled:opacity-50"
                  >
                    <MessageSquare size={13}/> {pending === `${item.kind}:sms` ? "..." : "SMS"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RoundTripModal({ booking, onCreate, onClose }) {
  // Defaults: next day, same time as the outbound pickup
  const srcDate = (booking.pickup_datetime || "").slice(0, 10);
  const srcTime = (booking.pickup_datetime || "").slice(11, 16) || "12:00";
  const nextDay = (() => {
    try {
      const d = new Date(`${srcDate}T00:00:00`);
      d.setDate(d.getDate() + 1);
      return d.toISOString().slice(0, 10);
    } catch { return srcDate; }
  })();
  const [date, setDate] = useState(nextDay);
  const [time, setTime] = useState(srcTime);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!date || !time) { toast.error("Pick the return date and time"); return; }
    setBusy(true);
    try { await onCreate(`${date}T${time}:00`); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="round-trip-modal">
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-md p-6 space-y-5" onClick={e => e.stopPropagation()}>
        <div>
          <div className="font-serif text-xl text-[#F5F2EB] flex items-center gap-2"><ArrowLeftRight size={16} className="text-[#D4AF37]"/> Create return trip</div>
          <p className="text-xs text-[#A1A1AA] mt-2">
            Pick when the return ride leaves. The route is reversed automatically and the fare is recalculated for the return leg.
          </p>
        </div>
        <div className="border border-white/10 bg-black/40 p-3 text-xs space-y-1.5">
          <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-16">Pickup</span>{booking.dropoff_address || "—"}</div>
          <div className="text-[#F5F2EB]"><span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] inline-block w-16">Drop-off</span>{booking.pickup_address || "—"}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] block mb-1.5">Return date</label>
            <input data-testid="rt-return-date" type="date" value={date} onChange={e => setDate(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm [color-scheme:dark]"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] block mb-1.5">Return time</label>
            <input data-testid="rt-return-time" type="time" value={time} onChange={e => setTime(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm [color-scheme:dark]"/>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button data-testid="rt-cancel" onClick={onClose} disabled={busy} className="btn-ghost !py-2 !px-4 text-[11px]">Cancel</button>
          <button data-testid="rt-create" onClick={create} disabled={busy || !date || !time} className="btn-gold !py-2 !px-5 text-[11px] disabled:opacity-40">
            {busy ? "Creating…" : "Create return trip"}
          </button>
        </div>
      </div>
    </div>
  );
}
